import Toast from 'react-native-toast-message'
import findAssociativeChanges from '../utils/findAssociativeChanges';
import { createNotification } from './notifications';
import { saveFolder } from './library';
import { getCurrentOrgID } from '../data/organization';
import * as Sentry from '@sentry/react-native';
import ErrorHandler from '../utils/ErrorHandler';
import { invokeEdgeFunction, queryClient, supabase } from '../data/client';
import { updateAssigneeResponse } from './tasks';
import { showAlert } from '../utils/showAlert';
import { insertActivity } from './activity';
import { buildUpdateDetails, buildCustomFieldDetails, joinDetails } from '../utils/buildActivityDetails';
import { isTransientError } from '@/utils/requestQueue';
import { QueryKeys } from '@/utils/react-query/QueryKeys';
import { getUpcomingDates } from '../utils/recurrence';
import { setEventServiceFlowTemplate, saveEventServiceFlowLocations, initializeServiceFlowScopes } from './serviceFlows';

/**
 * Helper to log event-related activity (fire-and-forget at call sites).
 * Server resolves org + actor from the JWT; summary is rendered at display time.
 * @param {string} verb - 'create'|'update'|'delete'
 * @param {string} eventID
 * @param {string} eventName
 * @param {string} [details] - optional human-readable description of what changed
 */
const logEventActivity = async (verb, eventID, eventName, details) => {
    await insertActivity({
        subjectType: 'Event',
        subjectID: eventID,
        subjectName: eventName,
        verb,
        details,
    });
};

/**
 * Cap on candidate ranges sent to check_location_conflicts (recurring series
 * can theoretically be unbounded — 200 occurrences is plenty to cover a year
 * of daily events).
 */
const LOCATION_CONFLICT_RANGE_CAP = 200;

/**
 * Ask the backend which of the candidate (location, range) pairs are already
 * booked. Wraps the check_location_conflicts RPC.
 *
 * Fails open: if the RPC errors we report it and return [] so a transient
 * failure can't block every event save.
 *
 * @param {Object} args
 * @param {string[]} args.locationIDs - candidate Location ids
 * @param {{start: (Date|string), end: (Date|string)}[]} args.ranges - candidate ranges
 * @param {string} [args.excludeEventID] - series to ignore (the event being edited)
 * @param {string[]} [args.excludeInstanceIDs] - occurrences to ignore (the instance being edited)
 * @returns {Promise<Array>} conflict rows {instanceID, eventID, eventName, locationID, locationName, startTime, endTime, allDay, rangeIndex}
 */
const checkLocationConflicts = async ({locationIDs, ranges, excludeEventID = null, excludeInstanceIDs = null}) => {
    const ids = [...new Set((locationIDs || []).filter(Boolean))];
    const validRanges = (ranges || []).filter(r => r?.start && !isNaN(new Date(r.start))).slice(0, LOCATION_CONFLICT_RANGE_CAP);

    if (!ids.length || !validRanges.length) return [];

    const {data, error} = await supabase.rpc('check_location_conflicts', {
        p_location_ids: ids,
        p_ranges: validRanges.map(r => {
            const start = new Date(r.start);
            const end = r.end && !isNaN(new Date(r.end)) && new Date(r.end) > start ? new Date(r.end) : new Date(start.getTime() + 60 * 60 * 1000);
            return `[${start.toISOString()},${end.toISOString()})`;
        }),
        p_exclude_event_id: excludeEventID || null,
        p_exclude_instance_ids: excludeInstanceIDs || null,
    });

    if(error){
        ErrorHandler(Error(error.message), {query: "checkLocationConflicts", input: {locationIDs: ids, rangeCount: validRanges.length, excludeEventID, excludeInstanceIDs}, error});
        return [];
    }

    return data || [];
}

/**
 * Build candidate ranges for conflict checking. Non-recurring → the single
 * [start, end) range. Recurring → expand occurrences via the rrule over the
 * recurrence window (recurrenceEndDate, else a 1-year horizon), capped.
 * allDay blocks the whole local day, mirroring the server-side semantics.
 */
const buildLocationCheckRanges = ({startTime, endTime, allDay = false, isRecurring = false, rruleString = null, recurrenceEndDate = null}) => {
    if(!startTime) return [];

    const start = new Date(startTime);
    if(isNaN(start)) return [];

    const durationMs = endTime && !isNaN(new Date(endTime)) ? Math.max(new Date(endTime).getTime() - start.getTime(), 0) : 0;

    const rangeFor = (occStart) => {
        if(allDay){
            const s = new Date(occStart);
            s.setHours(0, 0, 0, 0);
            const e = new Date(s);
            e.setDate(e.getDate() + 1);
            return {start: s, end: e};
        }
        return {
            start: occStart,
            end: durationMs > 0 ? new Date(occStart.getTime() + durationMs) : new Date(occStart.getTime() + 60 * 60 * 1000),
        };
    };

    if(isRecurring && rruleString){
        let horizon = recurrenceEndDate ? new Date(recurrenceEndDate) : null;
        if(!horizon || isNaN(horizon)){
            horizon = new Date(start);
            horizon.setFullYear(horizon.getFullYear() + 1);
        }

        const occurrences = getUpcomingDates(rruleString, LOCATION_CONFLICT_RANGE_CAP, start, horizon);
        if(!occurrences.length) return [rangeFor(start)];
        return occurrences.map(rangeFor);
    }

    return [rangeFor(start)];
}

/**
 * Surface location conflicts to the user and throw so the calling save path
 * aborts. The thrown error carries the conflicts for any UI that wants them.
 */
/**
 * One human-readable line per conflict row: time range first, then event
 * name, then location, comma-separated. Plain hyphens only (no em/en dashes).
 * Shared by the blocking save alert and the non-blocking form warnings.
 */
const formatLocationConflictLine = (c) => {
    const start = c.startTime ? new Date(c.startTime) : null;
    const end = c.endTime ? new Date(c.endTime) : null;
    const when = c.allDay
        ? 'All day'
        : `${start && !isNaN(start) ? start.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'}) : ''}${end && !isNaN(end) ? ` - ${end.toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}` : ''}`;
    return `• ${when}, ${c.eventName || 'Event'}, ${c.locationName || 'Location'}`;
};

/**
 * Date header line for a group of conflicts, e.g. "Sun, Jan 18, 2026".
 */
const formatLocationConflictDateHeader = (c) => {
    const start = c.startTime ? new Date(c.startTime) : null;
    return start && !isNaN(start)
        ? start.toLocaleDateString([], {weekday: 'short', month: 'short', day: 'numeric', year: 'numeric'})
        : 'Unknown date';
};

/**
 * Conflict rows sorted chronologically and rendered as date-grouped lines:
 * one header line per local calendar date, then one bullet line per conflict.
 * `maxEntries` caps conflict entries (headers are formatting, not counted);
 * overflow gets a "…and N more" line. Shared by describeLocationConflicts
 * and blockOnLocationConflicts.
 */
const buildLocationConflictLines = (conflicts, maxEntries) => {
    const sorted = [...(conflicts || [])].sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
    const lines = [];
    let lastHeader = null;
    sorted.slice(0, maxEntries).forEach(c => {
        const header = formatLocationConflictDateHeader(c);
        if(header !== lastHeader){
            lines.push(header);
            lastHeader = header;
        }
        lines.push(formatLocationConflictLine(c));
    });
    if(sorted.length > maxEntries){
        lines.push(`…and ${sorted.length - maxEntries} more`);
    }
    return lines;
};

/**
 * Joined multi-line summary of conflict rows, date-grouped and capped at
 * `maxLines` conflict entries with an overflow count line. Used by the
 * in-form conflict warnings.
 */
const describeLocationConflicts = (conflicts, maxLines = 4) => {
    return buildLocationConflictLines(conflicts, maxLines).join('\n');
};

const blockOnLocationConflicts = (conflicts) => {
    const MAX_LINES = 6;
    const lines = buildLocationConflictLines(conflicts, MAX_LINES);

    showAlert({
        title: "Location Conflict",
        subtitle: `Another event is already booked in this location:\n\n${lines.join('\n')}\n\nPick a different location or time, then save again.`,
        cancelText: "Got it",
        confirmText: null,
    });

    const conflictError = new Error("Location conflict");
    conflictError.locationConflicts = conflicts;
    throw conflictError;
}

/**
 * Fetch EventInstances occupying locations on a single calendar day, with each
 * instance's effective locations resolved (EventInstanceLocations overrides,
 * else series EventLocations, plus legacy locationID fields). Mirrors the
 * check_location_conflicts exclusion rules: canceled/deleted instances,
 * instances of deleted events, and small-group sessions are skipped.
 *
 * @param {Date} date - any time within the day to fetch
 * @returns {Promise<Array>} instances with `effectiveLocations` = [{id, name}]
 */
const getLocationScheduleForDay = async (date) => {
    const orgID = await getCurrentOrgID();
    if(!orgID) return [];

    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    const {error, data} = await supabase.from('EventInstances').select(`
        id,
        startTime,
        endTime,
        allDay,
        nameOverride,
        locationID,
        Event:Events!inner(id, name, isDeleted, defaultLocationID,
            Calendar:Calendars!inner(id, name, color),
            SmallGroup:SmallGroups(id, isDeleted),
            Locations:EventLocations(locationID, Location:Locations(id, name, detailedArea))),
        Locations:EventInstanceLocations(locationID, Location:Locations(id, name, detailedArea))
    `)
    .eq('organizationID', orgID)
    .eq('isDeleted', false)
    .eq('canceled', false)
    .eq('Events.Calendars.calendarType', 'EVENTS')
    .lt('startTime', dayEnd.toISOString())
    .or(`startTime.gte.${dayStart.toISOString()},endTime.gte.${dayStart.toISOString()}`)
    .order('startTime', {ascending: true});

    if(error){
        ErrorHandler(Error(error.message), {query: "getLocationScheduleForDay", input: {organizationID: orgID, date}, error}, `Something went wrong fetching the location schedule`);
        return [];
    }

    return (data || [])
        .filter(e => !e.Event?.isDeleted)
        .filter(e => {
            const smallGroups = e.Event?.SmallGroup;
            if(!smallGroups) return true;
            const list = Array.isArray(smallGroups) ? smallGroups : [smallGroups];
            return !list.some(sg => sg && sg.isDeleted === false);
        })
        .map(e => {
            const overrideLocs = (e.Locations || []).map(l => l.Location).filter(Boolean);
            const seriesLocs = (e.Event?.Locations || []).map(l => l.Location).filter(Boolean);
            return {
                ...e,
                effectiveLocations: overrideLocs.length > 0 ? overrideLocs : seriesLocs,
                effectiveLocationIDs: [...new Set([
                    ...(overrideLocs.length > 0 ? overrideLocs : seriesLocs).map(l => l.id),
                    e.locationID,
                    e.Event?.defaultLocationID,
                ].filter(Boolean))],
            };
        });
}

const getEvents = async ({recentOnly = false} = {}) => {
    const orgID = await getCurrentOrgID();

    // Public pages / pre-org-load fire this without an org context — an orgID
    // query would just raise `invalid input syntax for type uuid: "null"`.
    if(!orgID){
        return [];
    }

    // recentOnly: use the get_events_for_picker RPC. A client-side filter
    // (either a two-step .in('id', [...hundreds of UUIDs]) that exceeds
    // PostgREST's URL length limit → "Bad Request", or an embedded
    // EventInstances!inner join that makes supabase-js stack-overflow while
    // merging thousands of nested rows) doesn't scale, so the server does it.
    if(recentOnly){
        const {error, data} = await supabase.rpc('get_events_for_picker', {p_recent_only: true});

        if(error){
            if(!isTransientError(error)){
                ErrorHandler(Error(error.message), {query: "getEvents", input: {organizationID: orgID, recentOnly}, error}, `Something went wrong fetching the events`)
            }
            return [];
        }

        return data || [];
    }

    const {error, data} = await supabase.from('Events').select('*, Calendar:Calendars!inner(name, color, calendarType)').eq('organizationID', orgID,).eq('Calendars.calendarType', 'EVENTS').eq('isDeleted', false).order('name');

    if(error){
        if(!isTransientError(error)){
            ErrorHandler(Error(error.message), {query: "getEvents", input: {organizationID: orgID, recentOnly}, error}, `Something went wrong fetching the events`)
        }
        return [];
    }

    return data;
}

const getEvent = async (eventID) => {
    // Validate eventID is a UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!eventID || !uuidRegex.test(eventID)) {
        ErrorHandler(Error("Invalid event ID format"), {query: "getEvent", input: {eventID}}, `Invalid event ID provided`)
        return {};
    }

    const orgID = await getCurrentOrgID();
    const now = new Date();

    const {error, data} = await supabase.from('Events').select(`*,
        Locations:EventLocations(*, Location:Locations(*, Address:Addresses(*)), CheckInFilter:QuickFilters(*)),
        defaultSpeaker:People!public_Events_defaultSpeakerID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        CheckInFilter:QuickFilters(*),
        Calendar:Calendars(id, name, color, description, calendarType, isDeleted),
        Tasks:EventTasks(*, Task:Tasks(*), VolunteerTeam:VolunteerTeams(id, name)),
        EventInstances!inner(count)
    `).eq('id', eventID).eq('organizationID', orgID).eq('isDeleted', false).eq('EventInstances.isDeleted', false).lt('EventInstances.startTime', now.toISOString()).order('position', {referencedTable: 'Tasks', ascending: true}).maybeSingle();

    if(error){
        if(!error.message.includes('Could not find a relationship between')){
            ErrorHandler(Error(error.message), {query: "getEvent", input: {eventID, organizationID: orgID}, error}, `Something went wrong fetching the event`)
        }
        return {};
    }

    return {
        ...data,
        Tasks: data.Tasks?.map(t => ({...t.Task, taskID: t.taskID, position: t.position, additionalDetails: t.additionalDetails, time: t.time, volunteerTeamID: t.volunteerTeamID, VolunteerTeam: t.VolunteerTeam, id: t.id})),
        pastInstanceCount: data.EventInstances?.[0]?.count || 0
    };
}

const getEventInstance = async (eventInstanceID) => {
    const orgID = await getCurrentOrgID();
    // No org (logged out, e.g. queries firing on /auth/login before auth
    // resolves) — bail before org-scoped queries run. .eq('organizationID',
    // null) errors in Postgres: `invalid input syntax for type uuid: "null"`
    // (22P02). See CHURCH-APP-1ET.
    if (!orgID) return {};

    const {error, data} = await supabase.from('EventInstances').select(`*,
        Event:Events(*, defaultSpeaker:People!public_Events_defaultSpeakerID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), Calendar:Calendars(*), Locations:EventLocations(*, CheckInFilter:QuickFilters(id, name, filterJSON, logic), Location:Locations(*, Address:Addresses(*)))),
        Locations:EventInstanceLocations(*, CheckInFilter:QuickFilters(id, name, filterJSON, logic), Location:Locations(*, Address:Addresses(*))),
        speakers:People!EventSpeakers(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        Attendance(eventInstanceID, attendeeID, createdAt, checkedOutAt),
        Folder:Folders(*),
        Links(*),
        Songs(*, Folder:Folders(*, subfolders:Folders(*), files:Files(*)))
    `).eq('id', eventInstanceID).maybeSingle();

    if(error){
        if(!error.message.includes('Could not find a relationship between')){
            ErrorHandler(Error(error.message), {query: "getEventInstance", input: {eventInstanceID}, error}, `Something went wrong fetching the event`)
        }
        return {};
    }

    // Query TaskInstances separately to avoid schema cache relationship issues
    const {error: tiError, data: taskInstances} = await supabase
        .from('TaskInstances')
        .select(`*, Task:Tasks(*, VolunteerTeamTasks(*, VolunteerTeam:VolunteerTeams(id, name))), VolunteerTeam:VolunteerTeams(id, name, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)), Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))`)
        .eq('eventInstanceID', eventInstanceID)
        .eq('organizationID', orgID);

    if(tiError){
        ErrorHandler(Error(tiError.message), {query: "getEventInstanceTaskInstances", input: {eventInstanceID, organizationID: orgID}, error: tiError}, `Something went wrong fetching the event task instances`)
    }

    return {
        ...data,
        Event: data.Event,
        LocationOverrides: data.Locations?.map(l => ({
            ...l.Location,
            printLabelID: l.printLabelID,
            printFamilyLabelID: l.printFamilyLabelID,
            checkInFilterID: l.checkInFilterID,
            CheckInFilter: l.CheckInFilter
        })),
        Locations: data.Locations && data.Locations.length > 0
            ? data.Locations?.map(l => ({
                ...l.Location,
                printLabelID: l.printLabelID,
                printFamilyLabelID: l.printFamilyLabelID,
                checkInFilterID: l.checkInFilterID,
                CheckInFilter: l.CheckInFilter
            }))
            : data.Event?.Locations?.map(l => ({...l.Location, CheckInFilter: l.CheckInFilter, printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID})),
        TaskInstances: taskInstances?.map(t => ({...t, Task: {...t.Task, Assigners: t.Task?.Assigners?.filter(a => a.eventID == data?.eventID)}, Assignees: t.Assignees?.map(a => ({...a, ...a.Person}))})).sort((a, b) => {
            // Only volunteer tasks remain in TaskInstances; service flow items
            // are now in ServiceFlowInstances and read via the serviceFlows module.
            const aPos = a.Task?.VolunteerTeamTasks?.find(v => v.volunteerTeamID === a.volunteerTeamID)?.position ?? 0;
            const bPos = b.Task?.VolunteerTeamTasks?.find(v => v.volunteerTeamID === b.volunteerTeamID)?.position ?? 0;
            return aPos - bPos || (a.Task?.title || '').localeCompare(b.Task?.title || '');
        }),
    };
}


const getMonthlyEventInstances = async (month, year) => {
    const orgID = await getCurrentOrgID();
    if (!orgID) return [];
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0, 23, 59);

    const {error, data} = await supabase.from('EventInstances').select(`*,
        Event:Events!inner(*, defaultSpeaker:People!public_Events_defaultSpeakerID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), Calendar:Calendars!inner(*), CheckInFilter:QuickFilters(*), Locations:EventLocations(*, Location:Locations(*, Address:Addresses(*)))),
        Locations:EventInstanceLocations(*, CheckInFilter:QuickFilters(id, name, filterJSON, logic), Location:Locations(*, Address:Addresses(*))),
        TaskInstances(*, Task:Tasks(*))
    `)
    .eq('organizationID', orgID)
    .eq('isDeleted', false)
    .eq('Events.isDeleted', false)
    .eq('Events.Calendars.calendarType', 'EVENTS')
    .lt('startTime', lastDay.toISOString())
    .or('startTime.gt.' + firstDay.toISOString() + ', endTime.gt.' + firstDay.toISOString())
    .order('startTime', {ascending: true});

    if(error){
        if(!error.message.includes('Could not find a relationship between')){
            ErrorHandler(Error(error.message), {query: "getMonthlyEventInstances", input: {organizationID: orgID, month, year}, error}, `Something went wrong fetching the events`)
        }
        return [];
    }

    return data.map(e => ({
        ...e,
        LocationOverrides: e.Locations?.map(l => ({
            ...l.Location,
            printLabelID: l.printLabelID,
            printFamilyLabelID: l.printFamilyLabelID,
            checkInFilterID: l.checkInFilterID,
            CheckInFilter: l.CheckInFilter
        })),
        Locations: e.Locations && e.Locations.length > 0
            ? e.Locations?.map(l => ({
                ...l.Location,
                printLabelID: l.printLabelID,
                printFamilyLabelID: l.printFamilyLabelID,
                checkInFilterID: l.checkInFilterID,
                CheckInFilter: l.CheckInFilter
            }))
            : e.Event?.Locations?.map(l => ({...l.Location, printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID, CheckInFilter: l.CheckInFilter})),
        TaskInstances: e.TaskInstances
    }));
}


const getEventInstancesForDate = async (date) => {
    const orgID = await getCurrentOrgID();
    if (!orgID || !date) return [];

    const dayStart = new Date(date);
    dayStart.setHours(0, 0, 0, 0);
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);

    const {error, data} = await supabase.from('EventInstances').select(`*,
        Event:Events!inner(*, defaultSpeaker:People!public_Events_defaultSpeakerID_fkey(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), CheckInFilter:QuickFilters(*), Calendar:Calendars!inner(id, name, description, color, calendarType), Locations:EventLocations(*, Location:Locations(*, Address:Addresses(*)))),
        Locations:EventInstanceLocations(*, CheckInFilter:QuickFilters(id, name, filterJSON, logic), Location:Locations(*, Address:Addresses(*)))
    `)
    .eq('organizationID', orgID)
    .eq('isDeleted', false)
    .eq('Events.isDeleted', false)
    .eq('Events.Calendar.calendarType', 'EVENTS')
    .lt('startTime', dayEnd.toISOString())
    .or(`startTime.gte.${dayStart.toISOString()},endTime.gte.${dayStart.toISOString()}`)
    .order('startTime', {ascending: true});

    if(error){
        if(!error.message.includes('Could not find a relationship between')){
            ErrorHandler(Error(error.message), {query: "getEventInstancesForDate", input: {organizationID: orgID, date}, error}, `Something went wrong fetching the events`)
        }
        return [];
    }

    return data.map(e => ({
        ...e,
        LocationOverrides: e.Locations?.map(l => ({
            ...l.Location,
            printLabelID: l.printLabelID,
            printFamilyLabelID: l.printFamilyLabelID,
            checkInFilterID: l.checkInFilterID,
            CheckInFilter: l.CheckInFilter
        })),
        Locations: e.Locations && e.Locations.length > 0
            ? e.Locations?.map(l => ({
                ...l.Location,
                printLabelID: l.printLabelID,
                printFamilyLabelID: l.printFamilyLabelID,
                checkInFilterID: l.checkInFilterID,
                CheckInFilter: l.CheckInFilter
            }))
            : e.Event?.Locations?.map(l => ({...l.Location, printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID, CheckInFilter: l.CheckInFilter})),
    }));
}


const getEventInstancesForRegistration = async () => {
    const orgID = await getCurrentOrgID();
    const now = new Date();

    const {error, data} = await supabase.from('EventInstances').select(`*,
        Event:Events!inner(*, Calendar:Calendars!inner(id, name, description, color, calendarType))
    `).eq('organizationID', orgID).eq('isDeleted', false).eq('registrationEnabled', true).eq('Events.isDeleted', false).eq('Events.Calendar.calendarType', 'EVENTS').or(`startTime.gt.${now.toISOString()}, endTime.gt.${now.toISOString()}`).order('startTime', {ascending: true});

    if(error){
        ErrorHandler(Error(error.message), {query: "getEventInstancesForRegistration", input: {organizationID: orgID}, error}, `Something went wrong fetching event instances for registration`)
        return [];
    }

    return data || [];
}


const getEventCustomFields = async () => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('CustomFields').select('id, name, type, options, position, required, module, sectionID, Section:CustomFieldSections(*)').eq('organizationID', orgID).eq('module', 'Events').eq('isDeleted', false);

    if(error){
        ErrorHandler(Error(error.message), {query: "getEventCustomFields", input: {organizationID: orgID}, error}, `Something went wrong fetching the custom fields`)
        return [];
    }

    return data.sort((f1, f2) => (f1.Section?.position ?? 1000) - (f2.Section?.position ?? 1000) || (f1.position ?? 0) - (f2.position ?? 0) || f1.name.localeCompare(f2.name));
}


const getEventCustomFieldSections = async () => {
    const orgID = await getCurrentOrgID(); 

    const {error, data} = await supabase.from('CustomFieldSections').select('*, CustomFields(*)').eq('organizationID', orgID).eq('type', 'Events').order('position', {ascending: true}).order('position', {referencedTable: 'CustomFields', ascending: true});

    if(!error && data){
        data.forEach(section => {
            if(section.CustomFields){
                section.CustomFields = section.CustomFields.filter(field => !field.isDeleted);
            }
        });
    }

    if(error){
        ErrorHandler(Error(error.message), {query: "getEventCustomFieldSections", input: {organizationID: orgID}, error}, `Something went wrong fetching the custom field sections`)
        return [];
    }

    return data;
}

const getEventCustomFieldSection = async (id) => {
    const orgID = await getCurrentOrgID();

    const {error, data} = await supabase.from('CustomFieldSections').select('*, Fields:CustomFields(*)').eq('organizationID', orgID).eq('type', 'Events').eq('id', id).maybeSingle();

    if(!error && data && data.Fields){
        data.Fields = data.Fields.filter(field => !field.isDeleted);
    }

    if(error){
        ErrorHandler(Error(error.message), {query: "getEventCustomFieldSection", input: {organizationID: orgID, id}, error}, `Something went wrong fetching the custom field section`)
        return {};
    }

    return data;
}


const getAttendance = async (eventInstanceID) => {
    if (!eventInstanceID || eventInstanceID === 'undefined') {
        ErrorHandler(Error("Missing eventInstanceID"), {query: "getAttendance", eventInstanceID}, `Cannot fetch attendance without an event instance ID`);
        return [];
    }

    const {error, data} = await supabase.from('Attendance').select('attendeeID, Location:Locations(name), createdAt, checkedOutAt, labelPrinted, labelPrintCode, Submitter:People!submitterID(firstName, lastName), Attendee:People!attendeeID(firstName, lastName, dateOfBirth)').eq('eventInstanceID', eventInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getAttendance", eventInstanceID, error}, `Something went wrong fetching the attendance`)
        return [];
    }

    return data;
}

const getRecentAttendance = async ({pageParam = 0, eventID, searchTerm, eventInstanceID, signal}) => {
    // `signal` comes from React Query — superseded searches abort their
    // in-flight request instead of holding a request-queue slot (CHURCH-APP-14J).
    const {data: people, error} = await supabase.rpc('get_recent_attendees', {
        event_id: eventID,
        event_instance_id: eventInstanceID || null,
        search_term: searchTerm?.trim() || '',
        offset_rows: (pageParam * 25)
    }).abortSignal(signal);

    if(error){
        // Same aborted-superseded-fetch guard as getPeople — cancellations
        // surface here as errors but aren't real failures.
        if (signal?.aborted) return {nextPage: undefined, data: [], count: 0};
        ErrorHandler(Error(error.message), {query: "getRecentAttendance", input: {eventID, searchTerm, eventInstanceID}, error}, `Something went wrong fetching the recent attendance`);
        return {nextPage: undefined, data: [], count: 0};
    }

    const allData = !people ? [] : people?.results?.map(p => ({...p, HouseholdMembers: p.Address?.People?.filter(a => a.id != p.id).map(a => a)}))

    return {nextPage: allData.length < 25 ? undefined : pageParam + 1, data: allData, count: people?.totalCount || 0}
}

// Returns all EventInstance IDs in the extension set (main + extensions) for a
// given main instance. Calls the get_extension_instance_ids SQL RPC.
const getExtensionInstanceIDs = async (mainEventInstanceID) => {
    const { data, error } = await supabase
        .rpc('get_extension_instance_ids', { p_main_event_instance_id: mainEventInstanceID });

    if (error) {
        ErrorHandler(Error(error.message), { query: 'getExtensionInstanceIDs', mainEventInstanceID, error }, 'Failed to fetch extension instance IDs');
        return [mainEventInstanceID];
    }

    return data?.length ? data : [mainEventInstanceID];
};

// Resolves which EventInstance in the extension set a check-in should land on,
// based on time rules (current → latest start; else earliest future; else
// latest past). Calls the resolve_checkin_target SQL RPC — same logic the
// checkin-by-qr edge function uses. Returns null if no valid target found.
const resolveCheckinTarget = async (mainEventInstanceID) => {
    const { data, error } = await supabase
        .rpc('resolve_checkin_target', { p_main_event_instance_id: mainEventInstanceID })
        .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), { query: 'resolveCheckinTarget', mainEventInstanceID, error }, 'Failed to resolve check-in target');
        return null;
    }

    return data;
};

const addAttendance = async ({eventInstanceID, personID, submitterID, locationID}) => {
    if (!eventInstanceID) {
        ErrorHandler(Error("Missing eventInstanceID"), {mutation: "addAttendance", input: {eventInstanceID, personID, submitterID, locationID}}, `Cannot add attendance without an event instance ID`);
        throw Error("Missing eventInstanceID");
    }

    const orgID = await getCurrentOrgID();

    const input = {
        organizationID: orgID,
        eventInstanceID: eventInstanceID,
        attendeeID: personID,
        submitterID: submitterID,
        locationID: locationID
    }

    const {error, data} = await supabase.from('Attendance').upsert(input, {onConflict: 'eventInstanceID,attendeeID', ignoreDuplicates: true}).select('attendeeID, Location:Locations(name), createdAt, checkedOutAt, labelPrinted, labelPrintCode, Submitter:People!submitterID(firstName, lastName)').maybeSingle();

    if(error){
        if(!error.message.includes('duplicate key value violates unique constraint') && !error.message.includes('JSON object requested, multiple (or no) rows returned')){
            ErrorHandler(Error(error.message), {mutation: "addAttendance", input: input, error}, `Something went wrong adding the attendance record`);
            throw Error;
        }
    }

    return data;
}

const markAttendancePrinted = async ({attendeeIDs, eventInstanceID, labelPrintCode}) => {
    const update = {labelPrinted: true};
    if (labelPrintCode !== undefined) {
        update.labelPrintCode = labelPrintCode;
    }
    const {error, data} = await supabase.from('Attendance')
        .update(update)
        .in('attendeeID', attendeeIDs)
        .eq('eventInstanceID', eventInstanceID)
        .select('attendeeID, labelPrinted, labelPrintCode');

    if(error){
        ErrorHandler(Error(error.message), {mutation: "markAttendancePrinted", attendeeIDs, eventInstanceID, error}, `Something went wrong marking attendance as printed`);
        throw Error;
    }

    return data;
}

const removeAttendance = async ({attendeeID, eventInstanceID}) => {
    const {error} = await supabase.from('Attendance').delete().eq('attendeeID', attendeeID).eq('eventInstanceID', eventInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removeAttendance", attendeeID, eventInstanceID, error}, `Something went wrong removing the attendance record`);
        throw Error;
    }

    return {attendeeID}
}

// Removes attendance for a person across the entire extension set (main + all
// extensions) so "Remove Check In" works regardless of which instance the
// person was actually checked into.
const removeAttendanceFromExtensionSet = async ({attendeeID, mainEventInstanceID}) => {
    const instanceIDs = await getExtensionInstanceIDs(mainEventInstanceID);
    const {error} = await supabase.from('Attendance').delete().eq('attendeeID', attendeeID).in('eventInstanceID', instanceIDs);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removeAttendanceFromExtensionSet", attendeeID, mainEventInstanceID, instanceIDs, error}, `Something went wrong removing the attendance record`);
        throw Error;
    }

    return {attendeeID}
}

const checkOutAttendance = async ({attendeeID, eventInstanceID}) => {
    const {error, data} = await supabase.from('Attendance')
        .update({checkedOutAt: new Date().toISOString()})
        .eq('attendeeID', attendeeID)
        .eq('eventInstanceID', eventInstanceID)
        .select('attendeeID, Location:Locations(name), createdAt, checkedOutAt')
        .single();

    if(error){ 
        ErrorHandler(Error(error.message), {mutation: "checkOutAttendance", attendeeID, eventInstanceID, error}, `Something went wrong checking out`);
        throw Error;
    }

    return data;
}

const getPersonAttendanceHistory = async ({pageParam = 0, personID, eventName = null, startDate, endDate}) => {
    const orgID = await getCurrentOrgID();

    const query = supabase.from('Attendance')
        .select(`
            createdAt,
            checkedOutAt,
            EventInstance:EventInstances!inner(
                id,
                startTime,
                Event:Events!inner(id, name, Calendar:Calendars(name, color))
            ),
            Location:Locations(name)
        `)
        .eq('attendeeID', personID)
        .eq('organizationID', orgID)
        .order('createdAt', {ascending: false})
        .order('eventInstanceID', {ascending: false})
        .range(pageParam * 25, (pageParam * 25) + 24);

    if(eventName){
        query.eq('EventInstance.Event.name', eventName);
    }

    if(startDate){
        query.gte('EventInstance.startTime', startDate);
    }

    if(endDate){
        query.lte('EventInstance.startTime', endDate);
    }

    const {error, data} = await query;

    if(error){
        ErrorHandler(Error(error.message), {query: "getPersonAttendanceHistory", personID, error}, `Something went wrong fetching attendance history`);
        return {nextPage: undefined, data: []};
    }

    return {nextPage: data.length < 25 ? undefined : pageParam + 1, data: data || []};
}


const saveEventCustomField = async ({details}) => {
    const orgID = await getCurrentOrgID(); 

    let field = {
        id: details.id,
        name: details.name?.trim(),
        type: details.type,
        module: 'Events',
        sectionID: details.sectionID,
        options: details.options || [],
        organizationID: orgID
    }

    if(details.position !== undefined) field.position = details.position;
    if(details.required !== undefined) field.required = details.required;
    if(details.isProtected !== undefined) field.isProtected = details.isProtected;

    if(!field.id){
        return createEventCustomField(field); 
    }
    else{
        return updateEventCustomField(field);
    }
}


const createEventCustomField = async (field) => {
    const {error, data} = await supabase.from('CustomFields').insert(field).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createEventCustomField", input: field, error}, `Something went wrong creating that field`);
        throw Error;
    }

    Toast.delayShow({type: 'success',text2: `New Event field ${field.name} has been added successfully!`});
    return data;
}


const updateEventCustomField = async (field) => {
    const {error, data} = await supabase.from('CustomFields').update(field).eq('id', field.id).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateEventCustomField", input: field, error}, `Something went wrong updating that field`);
        throw Error;
    }

    Toast.delayShow({type: 'success',text2: `Event field was successfully updated!`});
    return data;
}


const saveEventCustomFieldSection = async (details) => {
    const orgID = await getCurrentOrgID(); 

    let field = {
        id: details.id,
        name: details.name,
        position: details.position !== undefined ? details.position : 1000,
        type: 'Events',
        organizationID: orgID
    }

    if(!field.id){
        return createEventCustomFieldSection(field, details.Fields); 
    }
    else{
        return updateEventCustomFieldSection(field, details.Fields);
    }
}


const createEventCustomFieldSection = async (section, fields = []) => {
    const {error, data} = await supabase.from('CustomFieldSections').insert(section).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createEventCustomFieldSection", input: section, error}, `Something went wrong creating that section`);
        throw Error;
    }

    const {error: error2, data: data2} = await supabase.from('CustomFields').update({sectionID: data.id}).in('id', fields?.map(f => f.id));

    if(error2){
        ErrorHandler(Error(error2.message), {mutation: "createEventCustomFieldSection", input: section, error: error2}, `Something went wrong updating the fields`);
        throw Error;
    }

    Toast.delayShow({type: 'success',text2: `New section ${section.name} has been added successfully for Events!`});
    return {...data, CustomFields: data2};
}


const updateEventCustomFieldSection = async (section, fields = []) => {
    const {error, data} = await supabase.from('CustomFieldSections').update(section).eq('id', section.id).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateEventCustomFieldSection", input: section, error}, `Something went wrong updating that section`);
        throw Error;
    }

    const {error: error2, data: data2} = await supabase.from('CustomFields').update({sectionID: section.id}).in('id', fields?.map(f => f.id));

    if(error2){
        ErrorHandler(Error(error2.message), {mutation: "updateEventCustomFieldSection", input: section, error: error2}, `Something went wrong updating the fields`);
        throw Error;
    }  

    Toast.delayShow({type: 'success',text2: `Section was successfully updated!`});
    return {...data, CustomFields: data2};
}


const deleteEventCustomField = async (id) => {
    const {error} = await supabase.from('CustomFields').update({isDeleted: true}).eq('id', id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteEventCustomField", id: id, error}, `Something went wrong deleting that field`);
        throw Error;
    }

    Toast.delayShow({type: 'success',text2: `This field was deleted successfully`});
}


const deleteEventCustomFieldSection = async (sectionID) => {
    const {error} = await supabase.from('CustomFieldSections').delete().eq('id', sectionID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteEventCustomFieldSection", input: sectionID, error}, `Something went wrong deleting that section`);
        throw Error;
    }

    Toast.delayShow({type: 'success',text2: `The section was deleted successfully`});
}


const deleteEvent = async (eventID) => {
    // Capture the name before deletion for the activity feed
    const {data: existing} = await supabase.from('Events').select('name').eq('id', eventID).maybeSingle();

    const {error} = await supabase.from('Events').delete().eq('id', eventID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteEvent", input: {id: eventID}, error}, `Something went wrong deleting that event`);
        throw Error;
    }

    Toast.delayShow({type: 'success', text2: `This event and all data have successfully been deleted`});

    logEventActivity('delete', eventID, existing?.name).catch(() => {});
}


const deleteEventTasks = async (eventID, tasksToDelete) => {
    const orgID = await getCurrentOrgID(); 

    if(!tasksToDelete || tasksToDelete.length === 0){
        return [];
    }

    // Delete by id (surrogate PK)
    for (const task of tasksToDelete) {
        const {error: error2} = await supabase.from('EventTasks').delete()
            .eq('id', task.id)
            .eq('organizationID', orgID);
        
        if(error2){
            ErrorHandler(Error(error2.message), {mutation: "deleteEventTasks", eventID, task, error: error2}, `Something went wrong removing the tasks from the event`)
            throw Error;
        }
    }

    const now = new Date();
    const {error: selectError, data: eventInstances} = await supabase.from('EventInstances').select('id, startTime, endTime').eq('eventID', eventID).eq('organizationID', orgID).gte('startTime', now.toISOString());
    if(selectError){
        ErrorHandler(Error(selectError.message), {mutation: "deleteEventTaskInstances", input: {eventID, tasksToDelete}, selectError}, `Something went wrong removing the tasks from the event`)
        throw Error;
    }

    // Delete task instances matching the removed taskID + team from future instances
    const futureInstanceIDs = eventInstances?.map(e => e.id) || [];
    if(futureInstanceIDs.length > 0){
        for(const task of tasksToDelete){
            let deleteQuery = supabase.from('TaskInstances').delete()
                .in('eventInstanceID', futureInstanceIDs)
                .eq('taskID', task.taskID)
                .eq("organizationID", orgID);

            if(task.volunteerTeamID){
                deleteQuery = deleteQuery.eq('volunteerTeamID', task.volunteerTeamID);
            } else {
                deleteQuery = deleteQuery.is('volunteerTeamID', null);
            }

            const {error: deleteError} = await deleteQuery;
            
            if(deleteError){
                ErrorHandler(Error(deleteError.message), {mutation: "deleteEventTaskInstances", input: {eventID, task}, deleteError}, `Something went wrong removing the tasks from the event`)
                throw Error;
            }
        }
    }
}


const createNewEventTasks = async (eventID, tasks = [], createTaskInstances = false) => {
    const orgID = await getCurrentOrgID(); 

    if(!tasks || tasks.length === 0){
        return [];
    }

    const eventTasks = tasks?.map(t => ({
        organizationID: orgID,
        taskID: t.id,
        eventID: eventID,
        volunteerTeamID: t.volunteerTeamID,
        // Only volunteer tasks carry time/additionalDetails/position on EventTasks;
        // service flow items now live in ServiceFlowEventDefaults (managed via RPC).
        ...(t.dueType === 'EVENT_VOLUNTEER' ? {
            time: t.time,
            additionalDetails: t.additionalDetails,
            position: t.position ?? 1000,
        } : {}),
    }))
    const {error: error2} = await supabase.from('EventTasks').insert(eventTasks);

    if(error2){
        ErrorHandler(Error(error2.message), {mutation: "createNewEventTasks", input: tasks, error: error2}, `Something went wrong adding the tasks to the event`)
        throw Error;
    }

    if(createTaskInstances){
        const eventInsatnceQuery = supabase.from("EventInstances").select('id, startTime, endTime').eq('eventID', eventID).eq('organizationID', orgID).eq('isDeleted', false);

        const now = new Date();
        eventInsatnceQuery.gte('startTime', now.toISOString());

        const {error, data: eventInstances} = await eventInsatnceQuery;

        if(error){
            ErrorHandler(Error(error.message), {mutation: "createNewEventTasks", error}, `Something went wrong adding the tasks to the event`)
            throw Error;
        }

        const taskInstances = eventInstances?.map(e => tasks?.map(t => ({
            taskID: t.id,
            organizationID: orgID,
            completed: false,
            eventInstanceID: e.id,
            dueDate: e.endTime || e.startTime,
            volunteerTeamID: t.volunteerTeamID,
            // Only volunteer tasks carry time/additionalDetails/position on TaskInstances;
            // service flow items now live in ServiceFlowInstances (managed via RPC).
            ...(t.dueType === 'EVENT_VOLUNTEER' ? {
                time: t.time,
                additionalDetails: t.additionalDetails,
                position: t.position ?? 1000,
            } : {}),
        }))).flat();

        // The unique index on TaskInstances(taskID, eventInstanceID, volunteerTeamID) is partial
        // (WHERE eventInstanceID IS NOT NULL AND isDeleted = false), which PostgREST can't infer
        // via onConflict. Filter existing non-deleted instances client-side, then plain insert.
        const taskIDs = [...new Set(tasks?.map(t => t.id))];
        const eventInstanceIDs = eventInstances?.map(e => e.id) ?? [];
        let toInsert = taskInstances;

        if(taskInstances.length > 0 && taskIDs.length > 0 && eventInstanceIDs.length > 0){
            const {data: existing, error: existingError} = await supabase
                .from('TaskInstances')
                .select('taskID, eventInstanceID, volunteerTeamID')
                .in('taskID', taskIDs)
                .in('eventInstanceID', eventInstanceIDs)
                .eq('isDeleted', false);

            if(existingError){
                ErrorHandler(Error(existingError.message), {mutation: "createNewEventTasks", error: existingError}, `Something went wrong adding the tasks to the event`)
                throw Error;
            }

            const existingKeys = new Set((existing ?? []).map(r => `${r.taskID}|${r.eventInstanceID}|${r.volunteerTeamID ?? ''}`));
            toInsert = taskInstances.filter(r => !existingKeys.has(`${r.taskID}|${r.eventInstanceID}|${r.volunteerTeamID ?? ''}`));
        }

        let error3 = null;
        if(toInsert.length > 0){
            const {error: insertError} = await supabase.from('TaskInstances').insert(toInsert);
            error3 = insertError;
        }

        if(error3){
            ErrorHandler(Error(error3.message), {mutation: "createNewEventTasks", input: toInsert, error: error3}, `Something went wrong adding the tasks to the event`)
            throw Error;
        }
    }

}


const saveEvent = async ({details, originalValues, creatorID}) => {
    const orgID = await getCurrentOrgID();
    const { data: org } = await supabase.from('Organizations').select('timezone').eq('id', orgID).single();
    const timezone = org?.timezone || 'UTC';

    // Service Flow v2 capability (spec §15.1). The serviceFlowSchemaVersion
    // column only exists after the conversion migration — a missing column or
    // any read failure means "not converted", and NO v2 write may be sent.
    let serviceFlowSchemaVersion = 1;
    const { data: orgV2, error: orgV2Error } = await supabase.from('Organizations').select('serviceFlowSchemaVersion').eq('id', orgID).maybeSingle();
    if (!orgV2Error) {
        serviceFlowSchemaVersion = orgV2?.serviceFlowSchemaVersion ?? 1;
    }
    const serviceFlowV2 = serviceFlowSchemaVersion === 2;
    
    // Helper to convert browser local time to organization timezone UTC using luxon
    const convertToOrgUTC = (date) => {
        if (!date) return null;
        const { DateTime } = require('luxon');
        
        // Convert the JavaScript Date to a luxon DateTime in the browser's local timezone
        const localDateTime = DateTime.fromJSDate(date);
        
        // Set the timezone to the organization's timezone
        const orgDateTime = localDateTime.setZone(timezone);
        
        // Convert to UTC
        const utcDateTime = orgDateTime.toUTC();
        
        // Convert back to JavaScript Date
        return utcDateTime.toJSDate();
    };

    let event = {
        organizationID: orgID,
        calendarID: details.calendarID, 
        defaultCustomFieldValues: details.defaultCustomFieldValues, 
        defaultEndTime: details.defaultEndTime ? convertToOrgUTC(new Date(details.defaultEndTime)) : null, 
        defaultNotes: details.defaultNotes, 
        defaultSpeakerID: details.defaultSpeakerID,
        defaultLocationID: details.defaultLocationID,
        defaultStartTime: details.defaultStartTime ? convertToOrgUTC(new Date(details.defaultStartTime)) : null,
        id: details.id, 
        isRecurring: details.isRecurring, 
        name: details.name?.trim(), 
        defaultAllDay: details.defaultAllDay,
        rruleString: details.rruleString,
        recurrenceDaysOfMonth: details.recurrenceDaysOfMonth, 
        recurrenceDaysOfWeek: details.recurrenceDaysOfWeek, 
        recurrenceEndDate: details.recurrenceEndDate, 
        recurrenceMonths: details.recurrenceMonths, 
        recurrenceWeeksOfMonth: details.recurrenceWeeksOfMonth, 
        recurringFrequency: details.recurringFrequency, 
        recurringInterval: details.recurringInterval, 
        recurringReferenceDate: details.recurringReferenceDate ? convertToOrgUTC(new Date(details.recurringReferenceDate)) : null,
        includeServiceFlowInExternalCalendar: details.includeServiceFlowInExternalCalendar,
        checkInFilterID: details.checkInFilterID,
        // Infrastructure is there for custom labels, but no editor yet, so just setting to the default ID's if they are checked
        printLabelID: details.printLabelID ? '87f89701-43eb-486d-a109-d4e03f7cb155' : null,
        printFamilyLabelID: details.printFamilyLabelID ? '678ae6f3-83b7-4ab3-af6b-3a92480b872b' : null
    }

    if(event.recurrenceEndDate){
        event.recurrenceEndDate = typeof event.recurrenceEndDate === 'string' ? event.recurrenceEndDate : event.recurrenceEndDate.toISOString().split('T')[0];
    }

    // Hard block on double-booking: any conflicting location aborts the save.
    const eventConflicts = await checkLocationConflicts({
        locationIDs: [
            ...(details.Locations || []).map(l => l?.id),
            event.defaultLocationID,
            details.locationID,
        ],
        ranges: buildLocationCheckRanges({
            startTime: event.defaultStartTime,
            endTime: event.defaultEndTime,
            allDay: event.defaultAllDay,
            isRecurring: event.isRecurring,
            rruleString: event.rruleString,
            recurrenceEndDate: event.recurrenceEndDate,
        }),
        excludeEventID: details.id || null,
    });
    if(eventConflicts.length > 0){
        blockOnLocationConflicts(eventConflicts);
    }

    // Service Flow attachment (spec §12.3): the initial INSERT may carry a
    // validated template ID (server still validates access) so the attachment
    // exists BEFORE occurrence generation. Later binding changes NEVER go
    // through this update — they use set_event_service_flow_template inside
    // updateEvent, so the two paths can't double-write.
    if(!details.id && serviceFlowV2 && details.serviceFlowTemplateID){
        event.serviceFlowTemplateID = details.serviceFlowTemplateID;
    }

    if(details.id){
        return updateEvent(event, details.Tasks, details.Locations, originalValues, details, serviceFlowV2)
    }
    else{
        return createEvent(event, details.Tasks, creatorID, details.Locations, serviceFlowV2 ? resolveServiceFlowLocationBindings(details) : [])
    }
}


/**
 * Propagate changes from the parent Event to all future EventInstances.
 * This updates startTime, endTime, notes, and customFieldValues for instances
 * that haven't occurred yet.
 */
const propagateEventChangesToFutureInstances = async (newDetails, oldData) => {
    const now = new Date();
    const orgID = await getCurrentOrgID();
    const { data: org } = await supabase.from('Organizations').select('timezone').eq('id', orgID).single();
    const timezone = org?.timezone || 'UTC';
    const { DateTime } = require('luxon');
    
    // Calculate the time difference if start/end times changed
    const oldStartTime = oldData.defaultStartTime ? new Date(oldData.defaultStartTime) : null;
    const newStartTime = newDetails.defaultStartTime ? new Date(newDetails.defaultStartTime) : null;
    const oldEndTime = oldData.defaultEndTime ? new Date(oldData.defaultEndTime) : null;
    const newEndTime = newDetails.defaultEndTime ? new Date(newDetails.defaultEndTime) : null;
    
    // Check if time-of-day changed (not the date, just the hours/minutes)
    // Use the organization's timezone to compare times
    const getTimeInTimezone = (date, tz) => {
        if (!date) return null;
        const dt = DateTime.fromJSDate(date).setZone(tz);
        return { hour: dt.hour, minute: dt.minute };
    };
    
    const oldTime = getTimeInTimezone(oldStartTime, timezone);
    const newTime = getTimeInTimezone(newStartTime, timezone);
    const oldEndTimeStr = getTimeInTimezone(oldEndTime, timezone);
    const newEndTimeStr = getTimeInTimezone(newEndTime, timezone);
    
    const startTimeChanged = oldTime && newTime && (
        oldTime.hour !== newTime.hour ||
        oldTime.minute !== newTime.minute
    );
    
    const endTimeChanged = oldEndTimeStr && newEndTimeStr && (
        oldEndTimeStr.hour !== newEndTimeStr.hour ||
        oldEndTimeStr.minute !== newEndTimeStr.minute
    );
    
    // Build the update object for future instances
    const instanceUpdate = {};
    
    // Update notes if changed
    if (newDetails.defaultNotes !== undefined) {
        instanceUpdate.notes = newDetails.defaultNotes;
    }
    
    // Update customFieldValues if changed
    if (newDetails.defaultCustomFieldValues !== undefined) {
        instanceUpdate.customFieldValues = newDetails.defaultCustomFieldValues;
    }
    
    // Update allDay if changed
    if (newDetails.defaultAllDay !== undefined) {
        instanceUpdate.allDay = newDetails.defaultAllDay ?? false;
    }
    
    // If only non-time fields changed, do a simple update
    if (!startTimeChanged && !endTimeChanged && Object.keys(instanceUpdate).length > 0) {
        const { error } = await supabase
            .from('EventInstances')
            .update(instanceUpdate)
            .eq('eventID', newDetails.id)
            .eq('organizationID', newDetails.organizationID)
            .gte('startTime', now.toISOString())
            .eq('isDeleted', false);
        
        if (error) {
            ErrorHandler(Error(error.message), { mutation: "propagateEventChangesToFutureInstances", input: instanceUpdate }, `Something went wrong updating future event instances`);
        }
    }
    
    // If time changed, we need to update each instance individually to adjust times
    if (startTimeChanged || endTimeChanged) {
        // Get all future instances
        const { data: futureInstances, error: fetchError } = await supabase
            .from('EventInstances')
            .select('id, startTime, endTime, startTimeOriginal, endTimeOriginal')
            .eq('eventID', newDetails.id)
            .eq('organizationID', newDetails.organizationID)
            .gte('startTime', now.toISOString())
            .eq('isDeleted', false);
        
        if (fetchError) {
            ErrorHandler(Error(fetchError.message), { mutation: "propagateEventChangesToFutureInstances", input: { eventID: newDetails.id } }, `Something went wrong fetching future event instances`);
            return;
        }
        
        // Update each instance
        for (const instance of futureInstances || []) {
            const updateData = { ...instanceUpdate };
            
            if (startTimeChanged && instance.startTime) {
                const instanceStart = new Date(instance.startTime);
                const instanceStartOriginal = instance.startTimeOriginal ? new Date(instance.startTimeOriginal) : null;
                
                // Get the instance date in organization's timezone
                const instanceDate = DateTime.fromJSDate(instanceStart).setZone(timezone);
                
                // Set the new time-of-day in the organization's timezone
                const newStartTimeOriginal = instanceDate.set({ hour: newTime.hour, minute: newTime.minute, second: 0 }).toUTC().toJSDate();
                updateData.startTimeOriginal = newStartTimeOriginal.toISOString();
                
                // Only update startTime if it matches startTimeOriginal (user hasn't modified this instance)
                const startTimeMatchesOriginal = instanceStartOriginal && 
                    instanceStart.getTime() === instanceStartOriginal.getTime();
                
                if (startTimeMatchesOriginal) {
                    updateData.startTime = newStartTimeOriginal.toISOString();
                }
            }
            
            if (endTimeChanged) {
                const instanceEnd = instance.endTime ? new Date(instance.endTime) : null;
                const instanceEndOriginal = instance.endTimeOriginal ? new Date(instance.endTimeOriginal) : null;
                const baseDate = instanceEnd || instance.startTime;
                
                // Get the instance date in organization's timezone
                const instanceDate = DateTime.fromJSDate(baseDate).setZone(timezone);
                
                // Set the new time-of-day in the organization's timezone
                const newEndTimeOriginal = instanceDate.set({ hour: newEndTimeStr.hour, minute: newEndTimeStr.minute, second: 0 }).toUTC().toJSDate();
                updateData.endTimeOriginal = newEndTimeOriginal.toISOString();
                
                // Only update endTime if it matches endTimeOriginal (user hasn't modified this instance)
                const endTimeMatchesOriginal = instanceEnd && instanceEndOriginal && 
                    instanceEnd.getTime() === instanceEndOriginal.getTime();
                
                if (endTimeMatchesOriginal) {
                    updateData.endTime = newEndTimeOriginal.toISOString();
                }
            }
            
            const { error: updateError } = await supabase
                .from('EventInstances')
                .update(updateData)
                .eq('id', instance.id);
            
            if (updateError) {
                ErrorHandler(Error(updateError.message), { mutation: "propagateEventChangesToFutureInstances", input: { instanceID: instance.id } }, `Something went wrong updating event instance`);
            }
        }
    }
};


// Location pickers default to explicit "None" — a selected location with no
// saved binding row must persist an explicit override so it can't silently
// keep inheriting the event default. Bindings for locations no longer on the
// event pass through untouched.
const resolveServiceFlowLocationBindings = (details) => {
    const existing = details?.serviceFlowLocations || [];
    const eventLocationIDs = new Set((details?.Locations || []).map(l => l?.id).filter(Boolean));
    const fromLocations = [...eventLocationIDs].map(locationID =>
        existing.find(b => b.locationID === locationID)
        ?? { locationID, isOverride: true, templateID: null, bindingVersion: 0 });
    return [...fromLocations, ...existing.filter(b => !eventLocationIDs.has(b.locationID))];
};

const updateEvent = async (details, tasks = [], locations = [], originalValues = null, rawDetails = null, serviceFlowV2 = false) => {
    const {error, data} = await supabase.from('Events').update(details).eq('id', details.id).select('Tasks:EventTasks(*), Locations(id)').single();
    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateEvent", input: {...details}, error}, `Something went wrong updating that event`)
        throw Error;
    }

    const oldEventData = originalValues || null;

    // Propagate changes to future EventInstances (compare new details with old data)
    if(oldEventData){
        await propagateEventChangesToFutureInstances(details, oldEventData);
    }

    const { itemsToAdd: locationsToAdd, itemsToDelete: locationsToDelete, unchangedItems: locationsToUpdate } = findAssociativeChanges(locations || [], data.Locations);
    // Task comparison by taskID + volunteerTeamID to support same task across multiple teams
    const makeTaskKey = (t) => `${t.taskID || t.id || ''}:${t.volunteerTeamID || ''}`;
    
    // Normalize DB tasks - they have taskID and id (EventTasks surrogate PK) from the EventTasks row
    const dbTasks = data.Tasks?.map(t => ({id: t.id, taskID: t.taskID, position: t.position, additionalDetails: t.additionalDetails, time: t.time, volunteerTeamID: t.volunteerTeamID})) || [];
    
    const taskKeys = new Set((tasks || []).map(makeTaskKey));
    const dbTaskKeys = new Set(dbTasks.map(makeTaskKey));
    
    const tasksToAdd = (tasks || []).filter(t => !dbTaskKeys.has(makeTaskKey(t)));
    const tasksToDelete = dbTasks.filter(t => !taskKeys.has(makeTaskKey(t)));
    const tasksToUpdate = (tasks || []).filter(t => dbTaskKeys.has(makeTaskKey(t)));


    if(locationsToAdd && locationsToAdd.length > 0){
        const {error: locationError} = await supabase.from('EventLocations').insert(locationsToAdd.map(l => ({eventID: details.id, locationID: l.id, organizationID: details.organizationID, printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID, checkInFilterID: l.checkInFilterID})));

        if(locationError){
            ErrorHandler(Error(locationError.message), {mutation: "createEventLocations", input: locations}, `Something went wrong adding the Locations to the new event`)
            throw Error()
        }
    }

    if(locationsToDelete && locationsToDelete.length > 0){
        const {error: locationError} = await supabase.from('EventLocations').delete().in('locationID', locationsToDelete?.map(l => l.id)).eq('eventID', details.id);

        if(locationError){
            ErrorHandler(Error(locationError.message), {mutation: "deleteEventLocations", input: locations}, `Something went wrong removing the Locations from the event`)
            throw Error()
        }
    }

    if(locationsToUpdate && locationsToUpdate.length > 0){
        locationsToUpdate.forEach(async l => {
            const {error: locationError} = await supabase.from('EventLocations').update({printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID, checkInFilterID: l.checkInFilterID}).eq('locationID', l.id).eq('eventID', details.id);

            if(locationError){
                ErrorHandler(Error(locationError.message), {mutation: "updateEventLocations", input: locations}, `Something went wrong updating the Locations from the event`)
                throw Error()
            }
        })
    }


    if(tasksToAdd && tasksToAdd.length > 0){
       await createNewEventTasks(details.id, tasksToAdd, true)
    }

    if(tasksToDelete && tasksToDelete.length > 0){
        await deleteEventTasks(details.id, tasksToDelete)
    }

    if(tasksToUpdate && tasksToUpdate.length > 0){
        tasksToUpdate.forEach(async t => {
            const updatePayload = {time: t.time, additionalDetails: t.additionalDetails, position: t.position, volunteerTeamID: t.volunteerTeamID};
            // Update by surrogate id
            const {error: taskError} = await supabase.from('EventTasks').update(updatePayload)
                .eq('id', t.id);

            if(taskError){
                ErrorHandler(Error(taskError.message), {mutation: "updateEventTasks", input: tasks}, `Something went wrong updating the Tasks from the event`)
                throw Error()
            }
        })
    }

    // TODO: VolunteerTeamAssigners are now per-team, not per-task+event.
    // Event create/edit should no longer manage assigners directly; they belong to VolunteerTeams.

    // Normalize a time value (ISO string | Date | null | undefined) to epoch ms.
    // Returns NaN for null/undefined so both-null compares as equal (NaN === NaN is false,
    // but we treat two falsy/NaN values as equal below).
    const toEpochMs = (v) => {
        if (v === null || v === undefined) return NaN;
        return new Date(v).getTime();
    };
    const sameTime = (a, b) => {
        const aMs = toEpochMs(a);
        const bMs = toEpochMs(b);
        if (Number.isNaN(aMs) && Number.isNaN(bMs)) return true;
        return aMs === bMs;
    };

    const recurrenceChanged = !oldEventData || (
        !sameTime(oldEventData.defaultStartTime, details.defaultStartTime) ||
        !sameTime(oldEventData.defaultEndTime, details.defaultEndTime) ||
        oldEventData.isRecurring !== details.isRecurring ||
        oldEventData.recurringFrequency !== details.recurringFrequency ||
        oldEventData.recurringInterval !== details.recurringInterval ||
        JSON.stringify(oldEventData.recurrenceDaysOfWeek) !== JSON.stringify(details.recurrenceDaysOfWeek) ||
        JSON.stringify(oldEventData.recurrenceWeeksOfMonth) !== JSON.stringify(details.recurrenceWeeksOfMonth) ||
        JSON.stringify(oldEventData.recurrenceMonths) !== JSON.stringify(details.recurrenceMonths) ||
        JSON.stringify(oldEventData.recurrenceDaysOfMonth) !== JSON.stringify(details.recurrenceDaysOfMonth) ||
        oldEventData.recurrenceEndDate !== details.recurrenceEndDate
    );

    // Compare current identity, but retain the version the editor actually
    // loaded: adopting a just-fetched token would overwrite concurrent edits.
    if(serviceFlowV2 && rawDetails && Object.prototype.hasOwnProperty.call(rawDetails, 'serviceFlowTemplateID')){
        const desiredTemplateID = rawDetails.serviceFlowTemplateID ?? null;
        const { data: bindingRow, error: bindingReadError } = await supabase
            .from('Events')
            .select('serviceFlowTemplateID, serviceFlowBindingVersion')
            .eq('id', details.id)
            .maybeSingle();

        if(bindingReadError || !bindingRow){
            const err = Error('Event details saved. Service Flow template could not be verified.');
            err.partialSave = true;
            ErrorHandler(err, { mutation: 'getEventServiceFlowAttachment', eventID: details.id, error: bindingReadError }, err.message);
            throw err;
        }
        if((bindingRow.serviceFlowTemplateID ?? null) !== desiredTemplateID){
            try {
                const result = await setEventServiceFlowTemplate({
                    eventID: details.id,
                    templateID: desiredTemplateID,
                    expectedBindingVersion: rawDetails.serviceFlowBindingVersion,
                    expectedTemplateVersion: rawDetails.serviceFlowTemplateVersion ?? null,
                });
                const p = result?.propagation;
                if(p && (p.protected || 0) > 0){
                    Toast.delayShow({ type: 'info', text2: `${p.protected} protected date${p.protected === 1 ? '' : 's'} kept their own flow.` });
                }
            } catch (attachError) {
                // Event fields saved; the attachment failed. Keep the draft
                // open for retry — the caller must not claim full success.
                const err = Error(attachError?.message || 'attachment failed');
                err.partialSave = true;
                ErrorHandler(err, { mutation: 'setEventServiceFlowTemplate', input: { eventID: details.id, desiredTemplateID } }, 'Event details saved. Service Flow template was not changed.');
                throw err;
            }
        }
    }

    const savedBindings = serviceFlowV2 ? await saveEventServiceFlowLocations(details.id, resolveServiceFlowLocationBindings(rawDetails)) : [];
    if((details.isRecurring && recurrenceChanged) || rawDetails?.serviceFlowSetupPending){
        // Retry edge function call for transient failures
        let instanceError = null;
        let retries = 0;
        const maxRetries = 3;
        while (retries <= maxRetries) {
            const result = await supabase.functions.invoke('create-event-instances-v2', {
                body: { eventID: details.id }
            });
            if (!result.error) {
                instanceError = null;
                break;
            }
            instanceError = result.error;
            retries++;
            if (retries <= maxRetries) {
                await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
            }
        }

        if(instanceError){
            instanceError.eventID = details.id;
            instanceError.serviceFlowLocations = savedBindings;
            ErrorHandler(Error(instanceError.message), {mutation: "createEventInstances", input: {eventID: details.id}}, `Something went wrong creating the event instances`)
            throw instanceError
        }
    }

    Toast.delayShow({type: 'success',text2: `The event "${details.name}" was updated successfully!`});

    // Log activity (fire and forget)
    const formatTime = (t) => t ? new Date(t).toLocaleString() : null;
    const resolveSpeakerName = (id, oldObj, newObj) => {
        const speaker = newObj?.defaultSpeaker || oldObj?.defaultSpeaker;
        return speaker ? `${speaker.firstName} ${speaker.lastName}`.trim() : null;
    };
    const resolveLocationName = (id, oldObj, newObj) => {
        const loc = newObj?.defaultLocation || oldObj?.defaultLocation;
        return loc ? loc.name : null;
    };
    let scalarDetails = oldEventData ? buildUpdateDetails(oldEventData, rawDetails || details, {
        name: { label: 'Name' },
        defaultStartTime: { label: 'Start time', format: formatTime },
        defaultEndTime: { label: 'End time', format: formatTime },
        defaultNotes: { label: 'Notes' },
        defaultAllDay: { label: 'All day' },
        isRecurring: { label: 'Recurring' },
        defaultSpeakerID: { label: 'Speaker', format: resolveSpeakerName },
        defaultLocationID: { label: 'Location', format: resolveLocationName },
    }) : null;

    // Resolve custom field names and diff them
    const eventCustomFieldIds = [...new Set([
        ...Object.keys(oldEventData?.defaultCustomFieldValues || {}),
        ...Object.keys(details.defaultCustomFieldValues || {}),
    ])];
    let eventCustomFieldDetails = null;
    if (eventCustomFieldIds.length > 0) {
        const { data: cfRows } = await supabase.from('CustomFields').select('id, name').in('id', eventCustomFieldIds).eq('organizationID', details.organizationID);
        const cfNameMap = Object.fromEntries((cfRows || []).map(cf => [cf.id, cf.name]));
        eventCustomFieldDetails = buildCustomFieldDetails(
            oldEventData?.defaultCustomFieldValues,
            details.defaultCustomFieldValues,
            cfNameMap,
        );
    }
    scalarDetails = joinDetails(scalarDetails, eventCustomFieldDetails);

    // Resolve volunteer team names for task additions/removals (no IDs in details)
    const changedTeamIDs = [...new Set([...tasksToAdd, ...tasksToDelete].map(t => t.volunteerTeamID).filter(Boolean))];
    let teamNameByID = {};
    if(changedTeamIDs.length > 0){
        const {data: teams} = await supabase.from('VolunteerTeams').select('id, name').in('id', changedTeamIDs);
        teamNameByID = Object.fromEntries((teams || []).map(t => [t.id, t.name]));
    }
    const addedTeamIDs = [...new Set(tasksToAdd.map(t => t.volunteerTeamID).filter(Boolean))];
    const removedTeamIDs = [...new Set(tasksToDelete.map(t => t.volunteerTeamID).filter(Boolean))];
    const taskDetails = [
        ...addedTeamIDs.filter(id => teamNameByID[id]).map(id => `- Added tasks for '${teamNameByID[id]}' Team`),
        ...removedTeamIDs.filter(id => teamNameByID[id]).map(id => `- Removed tasks for '${teamNameByID[id]}' Team`),
    ];

    logEventActivity('update', details.id, details.name, joinDetails(scalarDetails, taskDetails)).catch(() => {});
}


const createEvent = async (details, tasks = [], creatorID, locations = [], bindings = []) => {
    details.createdByID = creatorID;

    let instance = {
        organizationID: details.organizationID
    }

    if(details.defaultNotes){
        instance.notes = details.defaultNotes;
    }

    if(details.defaultStartTime){
        instance.startTime = details.defaultStartTime;
    }

    if(details.defaultEndTime){
        instance.endTime = details.defaultEndTime;
    }

    if(details.defaultAllDay){
        instance.allDay = details.defaultAllDay;
    }

    const {error, data} = await supabase.from('Events').insert(details).select('*').single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createEvent", input: {...details}}, `Something went wrong creating that event`)
        throw Error;
    }

    if(tasks && tasks.length > 0){
        try {
            await createNewEventTasks(data.id, tasks)
        } catch (taskError) {
            // The Event row already exists — retain its ID so a retry updates
            // this Event instead of inserting a duplicate (spec §12.3).
            // (callers sometimes `throw Error` — the constructor, not an
            // instance — so normalize before annotating.)
            const err = taskError instanceof globalThis.Error ? taskError : Error(taskError?.message || 'task creation failed');
            err.eventID = data.id;
            throw err;
        }

        // TODO: VolunteerTeamAssigners are now per-team, not per-task+event.
        // Event create/edit should no longer manage assigners directly; they belong to VolunteerTeams.
    }


    if(locations && locations.length > 0){
        const {error: locationError} = await supabase.from('EventLocations').insert(locations.map(l => ({eventID: data.id, locationID: l.id, organizationID: details.organizationID, printLabelID: l.printLabelID, printFamilyLabelID: l.printFamilyLabelID, checkInFilterID: l.checkInFilterID})));

        if(locationError){
            const locErr = Error(locationError.message);
            locErr.eventID = data.id;
            ErrorHandler(locErr, {mutation: "createEventLocations", input: locations}, `Something went wrong adding the Locations to the new event`)
            throw locErr;
        }
    }

    let savedBindings;
    try {
        savedBindings = await saveEventServiceFlowLocations(data.id, bindings);
    } catch (error) {
        error.eventID = data.id;
        throw error;
    }
    // Retry edge function call for transient failures
    let error4 = null;
    let retries = 0;
    const maxRetries = 3;
    while (retries <= maxRetries) {
        const result = await supabase.functions.invoke('create-event-instances-v2', {
            body: { eventID: data.id }
        });
        if (!result.error) {
            error4 = null;
            break;
        }
        error4 = result.error;
        retries++;
        if (retries <= maxRetries) {
            await new Promise(resolve => setTimeout(resolve, Math.pow(2, retries) * 1000));
        }
    }

    if(error4){
        const genErr = Error(error4.message);
        genErr.eventID = data.id;
        genErr.serviceFlowLocations = savedBindings;
        ErrorHandler(genErr, {mutation: "createEventInstances", input: {eventID: data.id}}, `Something went wrong creating the event instances`)
        throw genErr;
    }

    Toast.delayShow({type: 'success',text2: `Event created successfully!`});
    
    // Log activity (fire and forget - don't block on this)
    logEventActivity('create', data.id, data.name).catch(() => {
        // Silently fail - activity logging shouldn't break the main flow
    });
    
    return data;
}


const createAdditionalEventInstance = async ({eventID, date}) => {
    const orgID = await getCurrentOrgID();

    const {error: eventError, data: eventData} = await supabase.from('Events').select('*').eq('id', eventID).single();

    if(eventError){
        ErrorHandler(Error(eventError.message), {mutation: "createAdditionalEventInstance", input: {eventID, date}}, `Something went wrong adding a new occurrence for that event`);
        throw Error
    }

    const defaultStartTime = new Date(eventData.defaultStartTime);
    const newStartTime = new Date(date);
    newStartTime.setHours(defaultStartTime.getHours(), defaultStartTime.getMinutes(), defaultStartTime.getSeconds(), defaultStartTime.getMilliseconds());

    // Hard block on double-booking for the added occurrence.
    const {data: seriesLocationRows} = await supabase.from('EventLocations').select('locationID').eq('eventID', eventID);
    const durationMs = eventData.defaultEndTime ? Math.max(new Date(eventData.defaultEndTime) - defaultStartTime, 0) : 0;
    const instanceConflicts = await checkLocationConflicts({
        locationIDs: [
            ...(seriesLocationRows || []).map(l => l.locationID),
            eventData.defaultLocationID,
            eventData.locationID,
        ],
        ranges: buildLocationCheckRanges({
            startTime: newStartTime,
            endTime: durationMs > 0 ? new Date(newStartTime.getTime() + durationMs) : null,
            allDay: eventData.defaultAllDay,
        }),
        excludeEventID: eventID,
    });
    if(instanceConflicts.length > 0){
        blockOnLocationConflicts(instanceConflicts);
    }

    const {error: rpcError, data: instanceIDs} = await supabase.rpc('create_event_and_task_instances', {p_event_id: eventID, p_start_times: [newStartTime.toISOString()]});

    if(rpcError){
        ErrorHandler(Error(rpcError.message), {mutation: "createAdditionalEventInstance", input: {eventID, date}, error: rpcError}, `Something went wrong adding a new occurrence for that event`);
        throw Error
    }

    const newEventInstanceID = instanceIDs[0];

    if(eventData.defaultSpeakerID){
        const {error: error2} = await supabase.from('EventSpeakers').insert({organizationID: orgID, eventInstanceID: newEventInstanceID, personID: eventData.defaultSpeakerID});

        if(error2){
            ErrorHandler(Error(error2.message), {mutation: "createEventSpeakers", input: {organizationID: orgID, eventInstanceID: newEventInstanceID, personID: eventData.defaultSpeakerID}, error: error2}, `Something went wrong adding the speakers to the event`)
            throw Error;
        }
    }

    Toast.delayShow({type: 'success',text2: `New occurrence for "${eventData.name}" created successfully!`});
};


const cancelEventInstance = async ({eventInstanceID, canceled}) => {
    const {error, data} = await supabase.from('EventInstances').update({canceled: canceled}).eq('id', eventInstanceID).select().single();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "cancelEventInstance", input: {id: eventInstanceID, canceled: canceled}}, `Failed to ${canceled ? "cancel" : "reschedule"} this event`)
        throw Error;
    }

    Toast.show({text2: `Successfully ${canceled ? "canceled" : "rescheduled"} this event!`, type: 'success'})
    return data;
}


const deleteEventInstance = async ({eventInstanceID, isDeleted}) => {
    // Capture instance name + date before the change for the activity feed
    const {data: instance} = await supabase
        .from('EventInstances')
        .select('startTime, nameOverride, Event:Events(name)')
        .eq('id', eventInstanceID)
        .maybeSingle();

    const {error} = await supabase.from('EventInstances').update({isDeleted: isDeleted}).eq('id', eventInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteEventInstance", input: {id: eventInstanceID, isDeleted: isDeleted}}, `Failed to ${isDeleted ? "delete" : "restore"} this event`)
        throw Error;
    }

    const {error: error2} = await supabase.from('TaskInstances').update({isDeleted: isDeleted}).eq('eventInstanceID', eventInstanceID);

    if(error2){
        ErrorHandler(Error(error2.message), {mutation: "deleteEventInstance", input: {id: eventInstanceID, isDeleted: isDeleted}}, `Failed to ${isDeleted ? "delete" : "restore"} the tasks for this event`)
        throw Error;
    }

    Toast.show({text2: `Successfully ${isDeleted ? "deleted the occurrence" : "restored the occurrence"}!`, type: 'success'})

    // Only log occurrence deletions (restores are not user-facing activity)
    if(isDeleted){
        const baseName = instance?.nameOverride || instance?.Event?.name || 'Event';
        const dateStr = instance?.startTime ? new Date(instance.startTime).toLocaleDateString() : null;
        const instanceName = dateStr ? `${baseName} (${dateStr})` : baseName;
        insertActivity({
            subjectType: 'EventInstance',
            subjectID: eventInstanceID,
            subjectName: instanceName,
            verb: 'delete',
        }).catch(() => {});
    }
}


/**
 * Creates a new folder for an event and updates the associated event instance with the folder ID.
 * 
 * @param {Object} details - The details of the event instance.
 * @param {Object} details.Event - The event details.
 * @param {string} details.Event.name - The name of the event.
 * @param {string} details.startTime - The start time of the event instance.
 * @param {string} details.Event.createdByID - The ID of the user who created the event.
 * @param {string} details.id - The ID of the event instance to update.
 * 
 * @returns {Object} An object representing the created folder, including any subfolders and files.
 * 
 * @throws Will throw an error if the folder creation or event instance update fails.
 */
const createFolderForEvent = async ({details}) => {
    const newFolder = await saveFolder({name:details.Event?.name + "_" + details.startTime, createdByID:details.Event?.createdByID, noMessage: true})
    const updateEventInstanceInput = {id: details.id, folderID: newFolder.id}

    const {error} = await supabase.from('EventInstances').update(updateEventInstanceInput).eq('id', details.id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createFolderForEvent", input: {...updateEventInstanceInput, error}})
        throw Error;
    }

    return { 
        ...newFolder,
        subfolders: [],
        files: []
    }
}


const saveEventInstance = async (details) => {
    const orgID = await getCurrentOrgID();
    let serviceFlowV2 = false;
    try {
        const { data, error } = await supabase.from('Organizations').select('serviceFlowSchemaVersion').eq('id', orgID).maybeSingle();
        serviceFlowV2 = !error && data?.serviceFlowSchemaVersion === 2;
    } catch {
        // An unmigrated/offline capability read must never call an unavailable RPC.
    }

    const songs = details.songs ? details.songs : []
    const speakers = details.speakers ? details.speakers : []
    const links = details.links ? details.links : []
    const locations = details.LocationOverrides ? details.LocationOverrides : []
    const tasks = details.Tasks ? details.Tasks : []

    let event = {
        nameOverride: details.nameOverride,
        customFieldValues: details.customFieldValues, 
        notes: details.notes, 
        startTime: details.startTime,
        endTime: details.endTime,
        allDay: details.allDay,
        registrationEnabled: details.registrationEnabled ?? false,
        checkInPathID: details.checkInPathID || null,
    }

    // Hard block on double-booking: per-occurrence overrides win; with no
    // overrides the effective locations are the series' EventLocations (+ legacy fields).
    const overrideLocationIDs = (details.LocationOverrides || []).map(l => l?.id).filter(Boolean);
    const seriesLocationIDs = [
        ...(details.Event?.Locations || []).map(l => l?.locationID || l?.Location?.id || l?.id),
        details.Event?.defaultLocationID,
        details.Event?.locationID,
        details.locationID,
    ].filter(Boolean);

    const instanceConflicts = await checkLocationConflicts({
        locationIDs: overrideLocationIDs.length > 0 ? overrideLocationIDs : seriesLocationIDs,
        ranges: buildLocationCheckRanges({
            startTime: details.startTime,
            endTime: details.endTime,
            allDay: details.allDay,
        }),
        excludeInstanceIDs: details.id ? [details.id] : null,
    });
    if(instanceConflicts.length > 0){
        blockOnLocationConflicts(instanceConflicts);
    }

    let saved;
    if(details.id){
        event.id = details.id;
        saved = await updateEventInstance(event, tasks, speakers, songs, links, locations, orgID);
    }
    else{
        event.organizationID = orgID
        event.eventID = details.Event.id,
        event.startTimeOriginal = details.startTime;
        event.endTimeOriginal = details.endTime;

        saved = { id: await createEventInstance(tasks, event, speakers, songs, locations) };
    }
    try {
        if (serviceFlowV2) await initializeServiceFlowScopes(saved.id);
    } catch (error) {
        error.eventInstanceID = saved.id;
        error.partialSave = true;
        throw error;
    }
    if (!details.id) Toast.show({ type: 'success', text2: 'This occurrence was updated successfully!' });
    return saved;
}


const updateEventInstance = async (details, _tasks = [], speakers = [], songs = [], links = [], locations = [], orgID) => {
    // Build update object, explicitly including null values for endTime
    const updateData = {
        nameOverride: details.nameOverride,
        customFieldValues: details.customFieldValues,
        notes: details.notes,
        startTime: details.startTime,
        endTime: details.endTime,  // This will be null if user cleared it
        allDay: details.allDay,
        registrationEnabled: details.registrationEnabled ?? false,
        checkInPathID: details.checkInPathID || null,
    };
    
    // Only include id if present
    if (details.id) {
        updateData.id = details.id;
    }
    
    const {error, data} = await supabase.from('EventInstances').update(updateData).eq('id', details.id).select(`*, 
        speakers:People!EventSpeakers(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey), 
        Songs(id, name),
        Event:Events(id, name),
        Links(id, name),
        Locations(id),
        TaskInstances(*, Task:Tasks(*))
    `).maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateEventInstance", input: {...details}, error}, `Something went wrong updating that event occurrence`)
        throw Error;
    }

    //Update the due dates for any connected task instances
    const {error: taskInstanceDueDateError} = await supabase.from('TaskInstances').update({dueDate: data.startTime}).eq('eventInstanceID', data.id);

    if(taskInstanceDueDateError){
        ErrorHandler(Error(taskInstanceDueDateError.message), {mutation: "updateEventInstanceDueDate", input: {dueDate: data.startTime, eventInstanceID: data.id}})
    }

    // Service flow schedule changes are now handled by the save_service_flow_schedule RPC
    // (via src/data/serviceFlows.js). Volunteer task instances are managed directly
    // via createTaskInstance/deleteTaskInstance from the instance detail screen.

    const { itemsToAdd: speakersToAdd, itemsToDelete: speakersToDelete } = findAssociativeChanges(speakers, data.speakers);
    const { itemsToAdd: songsToAdd, itemsToDelete: songsToDelete } = findAssociativeChanges(songs, data.Songs);
    const { itemsToAdd: linksToAdd, itemsToDelete: linksToDelete } = findAssociativeChanges(links, data.Links);
    const { itemsToAdd: locationsToAdd, itemsToDelete: locationsToDelete, unchangedItems: existingLocations } = findAssociativeChanges(locations, data.Locations, "id");

    let eventSpeakerInsertError, eventSpeakerDeleteError, eventSongInsertError, eventSongDeleteError, eventLinkInsertError, eventLinkDeleteError, eventLocationInsertError, eventLocationDeleteError;
    
    if(speakersToAdd.length > 0){
        const {error} = await supabase.from('EventSpeakers').insert(speakersToAdd?.map(s => ({organizationID: orgID, eventInstanceID: details.id, personID: s.id})));
        eventSpeakerInsertError = error
    }

    if(songsToAdd.length > 0){
        const {error} = await supabase.from('EventSongs').insert(songsToAdd?.map(s => ({organizationID: orgID, eventInstanceID: details.id, songID: s.id, key: s.key})));
        eventSongInsertError = error
    }

    if(linksToAdd.length > 0){
        const {error} = await supabase.from('EventInstanceLinks').insert(linksToAdd?.map(l => ({organizationID: orgID, eventInstanceID: details.id, linkID: l.id})));
        eventLinkInsertError = error
    }

    if(speakersToDelete.length > 0){
        const {error} = await supabase.from('EventSpeakers').delete().eq('eventInstanceID', details.id).in('personID', speakersToDelete?.map(s => s.id));
        eventSpeakerDeleteError = error
    }

    if(songsToDelete.length > 0){
        const {error} = await supabase.from('EventSongs').delete().eq('eventInstanceID', details.id).in('songID', songsToDelete?.map(s => s.id));
        eventSongDeleteError = error
    }
    
    if(linksToDelete.length > 0){
        const {error} = await supabase.from('EventInstanceLinks').delete().eq('eventInstanceID', details.id).in('linkID', linksToDelete?.map(l => l.id));
        eventLinkDeleteError = error
    }

    if(locationsToAdd.length > 0){
        const {error} = await supabase.from('EventInstanceLocations').insert(locationsToAdd?.map(l => ({
            organizationID: orgID,
            eventInstanceID: details.id,
            locationID: l.id,
            printLabelID: l.printLabelID || null,
            printFamilyLabelID: l.printFamilyLabelID || null,
            checkInFilterID: l.checkInFilterID || null
        })));
        eventLocationInsertError = error
    }

    if(locationsToDelete.length > 0){
        const {error} = await supabase.from('EventInstanceLocations').delete().eq('eventInstanceID', details.id).in('locationID', locationsToDelete?.map(l => l.id));
        eventLocationDeleteError = error
    }

    // Update config fields for existing (unchanged) locations
    if(existingLocations.length > 0){
        for(const loc of existingLocations){
            const {error: locUpdateError} = await supabase.from('EventInstanceLocations').update({
                printLabelID: loc.printLabelID || null,
                printFamilyLabelID: loc.printFamilyLabelID || null,
                checkInFilterID: loc.checkInFilterID || null
            }).eq('eventInstanceID', details.id).eq('locationID', loc.id);

            if(locUpdateError){
                ErrorHandler(Error(locUpdateError.message), {mutation: "updateEventInstance_Locations", input: {...loc}, locUpdateError})
                throw locUpdateError;
            }
        }
    }
    
    if(eventSpeakerDeleteError || eventSpeakerInsertError || eventSongDeleteError || eventSongInsertError || eventLinkInsertError || eventLinkDeleteError || eventLocationInsertError || eventLocationDeleteError){
        ErrorHandler(Error((eventSpeakerDeleteError || eventSpeakerInsertError || eventSongDeleteError || eventSongInsertError || eventLinkInsertError || eventLinkDeleteError || eventLocationInsertError || eventLocationDeleteError).message), {mutation: "updateEventInstance", input: {...details}, eventSpeakerDeleteError, eventSpeakerInsertError, eventSongDeleteError, eventSongInsertError, eventLinkInsertError, eventLinkDeleteError, eventLocationInsertError, eventLocationDeleteError}, `We updated the occurrence, but there was an issue updating the speakers and/or songs. You might check your data and make sure everything is correct.`)
        throw Error;
    }

    return {...data, speakers: speakersToAdd, Songs: songsToAdd, Links: linksToAdd};
}


const createEventInstance = async (tasks, details, speakers = [], songs = [], locations = []) => {
    const {error, data} = await supabase.from('EventInstances').insert(details).select('*, Event:Events(name)').maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createEventInstance", input: details, error}, `Something went wrong creating that occurrence`)
        throw Error;
    }

    // Once the row exists, a failed setup retry must update it, not insert again.
    try {
    if(speakers?.length > 0){
        const speakersToInsert = speakers?.map(s => ({organizationID: details.organizationID, eventInstanceID: data.id, personID: s.id}));
        const {error: error2} = await supabase.from('EventSpeakers').insert(speakersToInsert);

        if(error2){
            ErrorHandler(Error(error2.message), {mutation: "createEventSpeakers", input: speakersToInsert, error: error2}, `Something went wrong adding the speakers to the event`)
            throw Error;
        }
    }

    if(songs?.length > 0){
        const newSongs = songs?.map(s => ({organizationID: details.organizationID, eventInstanceID: data.id, songID: s.id, key: s.key}));
        const {error: error3} = await supabase.from('EventSongs').insert(newSongs);

        if(error3){
            ErrorHandler(Error(error3.message), {mutation: "createEventSongs", input: newSongs, error: error3}, `Something went wrong adding the songs to the event`)
            throw Error;
        }
    }

    if(locations?.length > 0){
        const locationsToInsert = locations?.map(l => ({
            organizationID: details.organizationID,
            eventInstanceID: data.id,
            locationID: l.id,
            printLabelID: l.printLabelID || null,
            printFamilyLabelID: l.printFamilyLabelID || null,
            checkInFilterID: l.checkInFilterID || null
        }));
        const {error: error4} = await supabase.from('EventInstanceLocations').insert(locationsToInsert);

        if(error4){
            ErrorHandler(Error(error4.message), {mutation: "createEventInstanceLocations", input: locationsToInsert, error: error4}, `Something went wrong adding the locations to the event`)
            throw Error;
        }
    }

    if(tasks && tasks?.length > 0){
        tasks.forEach(async task => {
            const {data: newTaskInstance, error: error3} = await supabase.from('TaskInstances').insert({
                taskID: task.id,
                eventInstanceID: data.id,
                volunteerTeamID: task.volunteerTeamID,
                position: task.position ?? 1000,
                additionalDetails: task.additionalDetails,
                time: task.time ? (task.time instanceof Date ? task.time?.toTimeString().substring(0, 7) : task.time?.substring(0, 7)) : null,
                dueDate: data.startTime,
                organizationID: details.organizationID
            }).maybeSingle().select();

            if(task.TaskInstance?.Assignees?.length > 0 && !error3){
                
                task.TaskInstance?.Assignees.forEach(async assignee => {
                    const {error: error4} = await supabase.from('TaskInstancePeople').insert({taskInstanceID: newTaskInstance.id, personID: assignee.id, organizationID: details.organizationID, volunteerTeamID: task.volunteerTeamID || null});

                    if(error4){
                        ErrorHandler(Error(error4.message), {mutation: "updateEventInstance_Tasks", input: {...task}, error4})
                    }
                    else{
                        const eventDate = new Date(details.startTime)
                        const teamSuffix = task.VolunteerTeam?.name ? ` (${task.VolunteerTeam.name})` : ''
            
                        createNotification({
                            title: "You've been assigned a new task",
                            path: `tasks/instances/${newTaskInstance.id}`,
                            targetPersonID: assignee.id,
                            type: "TASK_ASSIGNED",
                            message: `${task.NewTask?.title}${teamSuffix} has been assigned to you for the event ${data.Event?.name} on ${eventDate.toLocaleString("en-US", {month:"2-digit", day:"2-digit", year:"numeric", hour:"numeric", minute:"2-digit"})}`,
                            parameters: {taskInstanceID: newTaskInstance.id},
                        })
                    }  
                })
            }
            else if(error3){
                ErrorHandler(Error(error3.message), {mutation: "updateEventInstance_Tasks", input: {...task}, error3})
            }
        })
    }

    return data.id;
    } catch (error) {
        const partialError = error instanceof Error ? error : Error(error?.message || 'Finishing occurrence setup failed');
        partialError.eventInstanceID = data.id;
        partialError.partialSave = true;
        throw partialError;
    }
}

const getBlackoutDates = async ({filter = 'active'} = {}) => {
    const orgID = await getCurrentOrgID();
    let query = supabase.from('BlackoutPeriods').select('*, Person:People(firstName, lastName)').eq('organizationID', orgID);
    
    if (filter === 'active') {
        query = query.gt('endDate', new Date().toISOString());
    }
    // If filter === 'all', don't apply any date filter
    
    const {error, data} = await query.order('startDate', {ascending: true});
    
    if(error){
        ErrorHandler(Error(error.message), {query: "getBlackoutDates", error, filter}, `Something went wrong fetching the blackout dates`)
        return [];
    }
    return data;
}   

const getBlackoutDatesForMonth = async ({month, year, personID = null}) => {
    const orgID = await getCurrentOrgID();
    const monthStart = new Date(year, month, 1).toISOString();
    const monthEnd = new Date(year, month + 1, 1).toISOString();

    let query = supabase.from('BlackoutPeriods').select('*, Person:People(firstName, lastName)')
        .eq('organizationID', orgID)
        .or(`and(startDate.gte.${monthStart},startDate.lt.${monthEnd}),and(endDate.gte.${monthStart},endDate.lt.${monthEnd}),and(startDate.lt.${monthStart},endDate.gte.${monthEnd})`);

    if (personID) {
        query = query.eq('personID', personID);
    }

    const {error, data} = await query;

    if(error){
        ErrorHandler(Error(error.message), {query: "getBlackoutDatesForMonth", input: {month, year, personID}, error}, `Something went wrong fetching the blackout dates`)
        return [];
    }

    return data;
}

const AsyncAlert = async (names) => new Promise((resolve) => {
    showAlert({
        title: "There are tasks that you are assigned to in the blackout period you are trying to create",
        subtitle: `These tasks must be declined to blackout these dates. Do you want to decline these task(s): ${names.join(", ")}`,
        onCancel: () => {
            resolve(false)
        },
        onConfirm: () => {
            resolve(true)
        }
    })
  });

const createBlackoutPeriod = async (details) => {
    details.organizationID = await getCurrentOrgID();
    const startDate = new Date(details.startDate).toISOString();
    const endDate = new Date(details.endDate).toISOString();
    const personIDs = details.personIDs || [details.personID];

    const {error: tasksError, data: tasks} = await supabase.from('TaskInstances').select('id, Assignee:TaskInstancePeople!inner(personID), Task:Tasks(title)').in("TaskInstancePeople.personID", personIDs).eq('organizationID', details.organizationID).eq('isDeleted', false).eq('completed', false).gt('dueDate', startDate).lt('dueDate', endDate);

    if(tasksError){
        ErrorHandler(Error(tasksError.message), {mutation: "createBlackoutPeriodGetTasks", ...details, tasksError}, `Something went wrong creating that blackout period`)
        throw Error;
    }
    else if(tasks?.length > 0){
        const names = tasks?.map(t => t.Task?.title);
        const confirmed = await AsyncAlert(names);

        if(confirmed){
            const loggedInUser = queryClient.getQueryData(QueryKeys.people.loggedInUser());
            tasks.forEach(t => {
                updateAssigneeResponse({personID: t.Assignee?.personID, taskInstanceID: t.id, newResponse: "DECLINED", reason: "", loggedInUserID: loggedInUser?.id})
            })
        }
        else{
            throw Error()
        }
    }

    const inserts = personIDs.map(personID => ({
        personID,
        startDate,
        endDate,
        organizationID: details.organizationID
    }));

    const {error, data} = await supabase.from('BlackoutPeriods').insert(inserts).select();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "createBlackoutPeriod", ...details, error}, `Something went wrong creating that blackout period`)
        throw Error;
    }

    return data
}

const updateBlackoutPeriod = async ({id, startDate, endDate}) => {
    const {error, data} = await supabase.from('BlackoutPeriods')
        .update({startDate: new Date(startDate).toISOString(), endDate: new Date(endDate).toISOString()})
        .eq('id', id)
        .select()
        .maybeSingle();

    if(error){
        ErrorHandler(Error(error.message), {mutation: "updateBlackoutPeriod", id, startDate, endDate, error}, `Something went wrong updating that blackout period`)
        throw Error;
    }

    return data;
}

const deleteBlackoutPeriod = async (id) => {
    const {error} = await supabase.from('BlackoutPeriods').delete().eq('id', id);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "deleteBlackoutPeriod", id, error}, `Something went wrong deleting that blackout period`)
        throw Error;
    }

    Toast.delayShow({type: 'success', text2: "Blackout period removed successfully"});
}

const getPrintLabels = async () => {
    const {error, data} = await supabase.from('Labels').select('*');
    if(error){
        ErrorHandler(Error(error.message), {query: "getPrintLabels", error}, `Something went wrong fetching the print labels`)
        return [];
    }
    return data;
}

const getPastEventInstances = async ({eventID, pageParam = 0}) => {
    const orgID = await getCurrentOrgID();
    const now = new Date();

    const {error, data} = await supabase.from('EventInstances').select(`
        id,
        startTime,
        endTime,
        nameOverride,
        canceled,
        isDeleted,
        Event:Events(id, name, Calendar:Calendars(name, color)),
        Attendance(count)
    `)
    .eq('eventID', eventID)
    .eq('organizationID', orgID)
    .eq('isDeleted', false)
    .lt('startTime', now.toISOString())
    .order('startTime', {ascending: false})
    .range(pageParam * 20, (pageParam * 20) + 19);

    if(error){
        ErrorHandler(Error(error.message), {query: "getPastEventInstances", input: {eventID, organizationID: orgID}, error}, `Something went wrong fetching past occurrences`);
        return {nextPage: undefined, data: []};
    }

    return {
        nextPage: data.length < 20 ? undefined : pageParam + 1,
        data: data.map(e => ({...e, attendanceCount: e.Attendance?.[0]?.count || 0}))
    };
}


const getEventInstancesByEventID = async (eventID) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase
        .from('EventInstances')
        .select('id, startTime, endTime, nameOverride, canceled, isDeleted, eventID, Event:Events(name)')
        .eq('eventID', eventID)
        .eq('organizationID', orgID)
        .eq('isDeleted', false)
        .order('startTime', { ascending: true });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getEventInstancesByEventID", input: { eventID, organizationID: orgID }, error }, `Something went wrong fetching event instances`);
        return [];
    }

    return data || [];
}

const getUpcomingEventInstancesWithTasks = async (eventID) => {
    const orgID = await getCurrentOrgID();
    const now = new Date().toISOString();
    const { data, error } = await supabase
        .from('EventInstances')
        .select(`*, Event:Events(name)`)
        .eq('organizationID', orgID)
        .eq('eventID', eventID)
        .gt('startTime', now)
        .eq('canceled', false)
        .eq('isDeleted', false)
        .order('startTime', { ascending: true })
        .limit(12);

    if (error) {
        ErrorHandler(Error(error.message), { query: "getUpcomingEventInstancesWithTasks", eventID, error }, `Something went wrong getting upcoming event instances`);
        return [];
    }

    // Query TaskInstances separately to avoid schema cache relationship issues
    const { data: taskInstances, error: tiError } = await supabase
        .from('TaskInstances')
        .select(`*, Task:Tasks(*, VolunteerTeamTasks(*, VolunteerTeam:VolunteerTeams(id, name, roleID, VolunteerTeamAssigners(personID)))), VolunteerTeam:VolunteerTeams(id, name, roleID, VolunteerTeamTasks(taskID, assigneesNeeded), VolunteerTeamAssigners(personID)), Assignees:TaskInstancePeople(*, Person:People(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey))`)
        .in('eventInstanceID', data?.map(d => d.id) || [])
        .eq('organizationID', orgID);

    if (tiError) {
        ErrorHandler(Error(tiError.message), { query: "getUpcomingEventInstancesWithTasks_TaskInstances", eventID, error: tiError }, `Something went wrong fetching task instances`);
    }

    const taskInstancesByEvent = {};
    taskInstances?.forEach(ti => {
        const eid = ti.eventInstanceID;
        if (!taskInstancesByEvent[eid]) taskInstancesByEvent[eid] = [];
        taskInstancesByEvent[eid].push({...ti, Assignees: ti.Assignees?.map(a => ({...a, ...a.Person}))});
    });

    // Sort task instances within each event by VolunteerTeamTasks.position (volunteer only;
    // service flow items are now in ServiceFlowInstances and read via the serviceFlows module).
    Object.values(taskInstancesByEvent).forEach(list => {
        list.sort((a, b) => {
            const aPos = a.Task?.VolunteerTeamTasks?.find(v => v.volunteerTeamID === a.volunteerTeamID)?.position ?? 0;
            const bPos = b.Task?.VolunteerTeamTasks?.find(v => v.volunteerTeamID === b.volunteerTeamID)?.position ?? 0;
            return aPos - bPos || (a.Task?.title || '').localeCompare(b.Task?.title || '');
        });
    });

    return data?.map(d => ({
        ...d,
        TaskInstances: taskInstancesByEvent[d.id] || []
    })) || [];
}

const getEventAnalytics = async (eventID, numberOfDays) => {
    // Validate eventID is a UUID
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!eventID || !uuidRegex.test(eventID)) {
        ErrorHandler(Error("Invalid event ID format"), {query: "getEventAnalytics", input: {eventID}}, `Invalid event ID provided`)
        return {avg: 0, max: 0, min: 0};
    }

    const {error, data} = await supabase.rpc('analytics_attendance_stats_by_event', {event_id: eventID, days: numberOfDays});

    if(error){
        ErrorHandler(Error(error.message), {query: "getEventAnalytics", eventID, error}, `Something went wrong fetching the event analytics`)
        return [];
    }

    return data?.[0]?.stats || {avg: 0, max: 0, min: 0};
}

const getEventUniqueAttendees = async (eventID, numberOfDays) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!eventID || !uuidRegex.test(eventID)) {
        ErrorHandler(Error("Invalid event ID format"), {query: "getEventUniqueAttendees", input: {eventID}}, `Invalid event ID provided`);
        return 0;
    }

    const {error, data} = await supabase.rpc('analytics_unique_attendees_by_event', {event_id: eventID, days: String(numberOfDays)});

    if (error) {
        ErrorHandler(Error(error.message), {query: "getEventUniqueAttendees", eventID, error}, `Something went wrong fetching the unique attendees`);
        return 0;
    }

    return data || 0;
}

const getEventUniqueAttendeesDetail = async (eventID, numberOfDays) => {
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!eventID || !uuidRegex.test(eventID)) {
        ErrorHandler(Error("Invalid event ID format"), {query: "getEventUniqueAttendeesDetail", input: {eventID}}, `Invalid event ID provided`);
        return [];
    }

    const {error, data} = await supabase.rpc('analytics_unique_attendees_by_event_detail', {event_id: eventID, days: String(numberOfDays)});

    if (error) {
        ErrorHandler(Error(error.message), {query: "getEventUniqueAttendeesDetail", eventID, error}, `Something went wrong fetching the unique attendees detail`);
        return [];
    }

    return data || [];
}

const getEventForMerge = async (id) => {
    const orgID = await getCurrentOrgID();

    const { error, data } = await supabase.from('Events').select(`
        id, name,
        Calendar:Calendars(id, name, color),
        EventInstances(id, startTime, endTime),
        Locations:EventLocations(*, Location:Locations(*))
    `)
    .eq('organizationID', orgID)
    .eq('id', id)
    .maybeSingle();

    if (error) {
        ErrorHandler(Error(error.message), {query: "getEventForMerge", input: {id}}, `Something went wrong fetching Event for merge`);
        return null;
    }

    return data;
}

const mergeEvents = async ({ sourceId, targetId, targetLocationId }) => {
    const orgID = await getCurrentOrgID();

    // Get all instances of Event A (source) - just id/time; no need to pull Attendance rows
    const { data: sourceInstances, error: sourceError } = await supabase
        .from('EventInstances')
        .select('id, startTime, endTime')
        .eq('eventID', sourceId)
        .eq('organizationID', orgID)
        .eq('isDeleted', false);

    if (sourceError) {
        ErrorHandler(Error(sourceError.message), {mutation: "mergeEvents-fetchSource", sourceError}, `Something went wrong fetching source event instances`);
        throw Error();
    }

    // Get all instances of Event B (target)
    const { data: targetInstances, error: targetError } = await supabase
        .from('EventInstances')
        .select('id, startTime, endTime')
        .eq('eventID', targetId)
        .eq('organizationID', orgID)
        .eq('isDeleted', false);

    if (targetError) {
        ErrorHandler(Error(targetError.message), {mutation: "mergeEvents-fetchTarget", targetError}, `Something went wrong fetching target event instances`);
        throw Error();
    }

    // Process each source instance
    for (const sourceInstance of sourceInstances || []) {
        const sourceStartTime = new Date(sourceInstance.startTime);
        const oneHourBefore = new Date(sourceStartTime.getTime() - 60 * 60 * 1000);
        const oneHourAfter = new Date(sourceStartTime.getTime() + 60 * 60 * 1000);

        // Find a target instance within 1 hour of the source instance time
        const matchingTargetInstance = targetInstances?.find(target => {
            const targetTime = new Date(target.startTime);
            return targetTime >= oneHourBefore && targetTime <= oneHourAfter;
        });

        let targetInstanceID;

        if (matchingTargetInstance) {
            // Use existing target instance
            targetInstanceID = matchingTargetInstance.id;
        } else {
            // Create new instance for Event B at the source instance's time
            const { data: newInstance, error: createError } = await supabase
                .from('EventInstances')
                .insert({
                    organizationID: orgID,
                    eventID: targetId,
                    startTime: sourceInstance.startTime,
                    endTime: sourceInstance.endTime,
                    isDeleted: false
                })
                .select('id')
                .single();

            if (createError) {
                ErrorHandler(Error(createError.message), {mutation: "mergeEvents-createInstance", createError}, `Something went wrong creating new instance`);
                continue; // Skip this instance but continue with others
            }

            targetInstanceID = newInstance.id;
        }

        // If no existing target instance, we can safely mass-update all source attendance
        // If there is an existing target instance, delete source rows whose attendeeID
        // already exists in the target to avoid violating the (eventInstanceID, attendeeID) unique constraint
        if (matchingTargetInstance) {
            const { data: existingTargetAttendees, error: existingError } = await supabase
                .from('Attendance')
                .select('attendeeID')
                .eq('eventInstanceID', targetInstanceID);

            if (existingError) {
                ErrorHandler(Error(existingError.message), {mutation: "mergeEvents-fetchExistingAttendance", existingError}, `Something went wrong checking existing attendance`);
                continue;
            }

            const existingAttendeeIDs = (existingTargetAttendees || []).map(a => a.attendeeID);

            if (existingAttendeeIDs.length > 0) {
                // Delete duplicate attendance from source (they're already in target)
                const { error: dedupeError } = await supabase
                    .from('Attendance')
                    .delete()
                    .eq('eventInstanceID', sourceInstance.id)
                    .in('attendeeID', existingAttendeeIDs);

                if (dedupeError) {
                    ErrorHandler(Error(dedupeError.message), {mutation: "mergeEvents-dedupeAttendance", dedupeError}, `Something went wrong removing duplicate attendance`);
                    continue;
                }
            }
        }

        // Mass update remaining Attendance rows from source -> target instance + location
        const { error: updateError } = await supabase
            .from('Attendance')
            .update({ eventInstanceID: targetInstanceID, locationID: targetLocationId })
            .eq('eventInstanceID', sourceInstance.id);

        if (updateError) {
            ErrorHandler(Error(updateError.message), {mutation: "mergeEvents-updateAttendance", updateError}, `Something went wrong updating attendance records`);
            continue;
        }
    }

    // Mark Event A as deleted (soft delete)
    const { error: deleteError } = await supabase
        .from('Events')
        .update({ isDeleted: true })
        .eq('id', sourceId);

    if (deleteError) {
        ErrorHandler(Error(deleteError.message), {mutation: "mergeEvents-delete", deleteError}, `Something went wrong marking the source Event as deleted`);
        throw Error();
    }

    Toast.delayShow({ type: 'success', text2: 'Events merged successfully!' });
    return { sourceId, targetId };
}

const getEventRegistrants = async (eventInstanceID) => {
    // Fetch registrants with location info
    const {error, data: registrants} = await supabase.from('EventRegistrants').select(`
        personID,
        registeredAt,
        source,
        notes,
        groupID,
        Person:People!personID(id, firstName, lastName, avatarUpdatedAt, avatarStorageKey),
        RegisteredBy:People!registeredByID(firstName, lastName)
    `).eq('eventInstanceID', eventInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {query: "getEventRegistrants", eventInstanceID, error}, `Something went wrong fetching the event registrants`)
        return [];
    }

    if (!registrants?.length) return [];

    // Fetch attendance across the extension set (main + extensions) so a
    // registrant checked into any sibling instance shows as checked in here.
    const personIDs = registrants.map(r => r.personID);
    const instanceIDs = await getExtensionInstanceIDs(eventInstanceID);
    const {data: attendance} = await supabase.from('Attendance')
        .select('attendeeID, checkedOutAt, Location:Locations(name)')
        .in('eventInstanceID', instanceIDs)
        .in('attendeeID', personIDs);



    // Add isCheckedIn and sort: unchecked first, then alphabetically by first name
    return registrants
        .map(r => ({
            ...r,
            isCheckedIn: attendance?.some(a => a.attendeeID === r.personID && !a.checkedOutAt),
            locationName: attendance?.find(a => a.attendeeID === r.personID)?.Locations?.Location?.name || null
        }))
        .sort((a, b) => {
            // Unchecked first
            if (a.isCheckedIn !== b.isCheckedIn) return a.isCheckedIn ? 1 : -1;
            // Then alphabetically by first name
            return (a.Person?.firstName || '').localeCompare(b.Person?.firstName || '');
        });
}

// Registers one or more people as a single group (shared groupID + qrToken).
// emailRecipientPersonID (optional) must be one of personIDs — the edge
// function sends exactly one confirmation email to that person; when omitted
// no email is sent. Returns the full response: { success, groupID, qrToken,
// results: [{personID, status}], emailSent, emailRecipientPersonID }.
const addEventRegistrant = async ({eventInstanceID, personIDs, emailRecipientPersonID}) => {
    const data = await invokeEdgeFunction('register-for-event', {
        body: {eventInstanceID, personIDs, emailRecipientPersonID},
    }, {
        mutation: 'addEventRegistrant',
        context: {eventInstanceID, personIDs, emailRecipientPersonID},
        userFriendlyMessage: 'Something went wrong adding the event registrant',
    });

    return data ?? null;
}

const getEventRegistrantCodes = async ({ eventInstanceID, personID }) => {
    const orgID = await getCurrentOrgID();
    const { error, data } = await supabase
        .from('EventRegistrationCodes')
        .select('id, code, isActive, revokedAt, createdAt, createdByPersonID, CreatedBy:People!EventRegistrationCodes_createdByPersonID_fkey(firstName, lastName)')
        .eq('organizationID', orgID)
        .eq('eventInstanceID', eventInstanceID)
        .eq('personID', personID)
        .order('createdAt', { ascending: false });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getEventRegistrantCodes", eventInstanceID, personID, error }, `Something went wrong fetching the registrant codes`);
        return [];
    }

    return data;
}

const getEventRegistrantPaths = async ({ eventInstanceID }) => {
    const { error, data } = await supabase
        .from('EventRegistrantPaths')
        .select('id, name, pathID, createdAt, Path:Paths!EventRegistrantPaths_pathID_fkey(id, name, isActive)')
        .eq('eventInstanceID', eventInstanceID)
        .order('createdAt', { ascending: true });

    if (error) {
        ErrorHandler(Error(error.message), { query: "getEventRegistrantPaths", eventInstanceID, error }, `Something went wrong fetching the registrant paths`);
        return [];
    }

    // Guard against dangling pathID (shouldn't happen due to CASCADE, but filter anyway)
    return (data || []).filter(row => row.Path);
}

const addEventRegistrantPath = async ({ eventInstanceID, name, pathID, createdByPersonID }) => {
    const orgID = await getCurrentOrgID();

    const input = {
        organizationID: orgID,
        eventInstanceID,
        name,
        pathID,
    };
    if (createdByPersonID) input.createdByPersonID = createdByPersonID;

    const { error, data } = await supabase.from('EventRegistrantPaths').insert(input).select('id, name, pathID').single();

    if (error) {
        const isDuplicate = error.code === '23505' ||
            (error.message && error.message.includes('duplicate key value'));
        if (isDuplicate) {
            throw Error('That path is already linked.');
        }
        ErrorHandler(Error(error.message), { mutation: "addEventRegistrantPath", input, error }, `Something went wrong linking the registrant path`);
        throw error;
    }

    return data;
}

const removeEventRegistrantPath = async ({ id }) => {
    const { error } = await supabase.from('EventRegistrantPaths').delete().eq('id', id);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "removeEventRegistrantPath", id, error }, `Something went wrong removing the registrant path`);
        throw error;
    }
}

const getRegistrantPathEnrollments = async ({ eventInstanceID, personID }) => {
    // 1. Get linked pathIDs for this event instance
    const { data: registrantPaths, error: pathsError } = await supabase
        .from('EventRegistrantPaths')
        .select('pathID')
        .eq('eventInstanceID', eventInstanceID);

    if (pathsError) {
        ErrorHandler(Error(pathsError.message), { query: "getRegistrantPathEnrollments", eventInstanceID, personID, error: pathsError }, `Something went wrong fetching registrant path enrollments`);
        return {};
    }

    const pathIDs = (registrantPaths || []).map(r => r.pathID);
    if (pathIDs.length === 0) return {};

    // 2. Fetch ACTIVE enrollments for this person on those paths
    const { error, data } = await supabase
        .from('PathEnrollments')
        .select('id, pathID, status')
        .eq('personID', personID)
        .in('pathID', pathIDs)
        .eq('status', 'ACTIVE');

    if (error) {
        ErrorHandler(Error(error.message), { query: "getRegistrantPathEnrollments", eventInstanceID, personID, error }, `Something went wrong fetching registrant path enrollments`);
        return {};
    }

    // Returns map pathID → enrollment
    const map = {};
    (data || []).forEach(e => { map[e.pathID] = e; });
    return map;
}

// Registrant detail for the scan-flow modal: registration meta + check-in status
// in one fetch. Used so the modal doesn't depend on the scan payload (which only
// carries personID/firstName/lastName/dateOfBirth) for registeredAt/source or
// for the already-checked-in state on badge scans.
// Registration meta always reads from eventInstanceID (the registration-OWNING
// instance). Attendance reads from attendanceEventInstanceID when provided —
// the check-in TARGET, which may be a different instance in the registration
// extension set than the one owning the registration.
const getRegistrantDetail = async ({ eventInstanceID, personID, attendanceEventInstanceID }) => {
    const { data: registrant, error: regError } = await supabase
        .from('EventRegistrants')
        .select('registeredAt, source, groupID, RegisteredBy:People!registeredByID(firstName, lastName)')
        .eq('eventInstanceID', eventInstanceID)
        .eq('personID', personID)
        .maybeSingle();

    const { data: attendance, error: attError } = await supabase
        .from('Attendance')
        .select('checkedOutAt, Location:Locations(name)')
        .eq('eventInstanceID', attendanceEventInstanceID || eventInstanceID)
        .eq('attendeeID', personID)
        .maybeSingle();

    if (regError) {
        ErrorHandler(Error(regError.message), { query: 'getRegistrantDetail', eventInstanceID, personID, error: regError }, 'Something went wrong fetching the registrant detail');
    }
    if (attError) {
        ErrorHandler(Error(attError.message), { query: 'getRegistrantDetail', eventInstanceID, personID, attendanceEventInstanceID, error: attError }, 'Something went wrong fetching the registrant attendance');
    }

    const activeAttendance = attendance && !attendance.checkedOutAt ? attendance : null;
    return {
        registeredAt: registrant?.registeredAt ?? null,
        source: registrant?.source ?? null,
        groupID: registrant?.groupID ?? null,
        registeredByName: registrant?.RegisteredBy ? `${registrant.RegisteredBy.firstName ?? ''} ${registrant.RegisteredBy.lastName ?? ''}`.trim() : null,
        isCheckedIn: !!activeAttendance,
        locationName: activeAttendance?.Location?.name ?? null,
    };
}

const enrollRegistrantInPath = async ({ pathID, personID, enrolledBy, eventInstanceID, eventRegistrantPathID }) => {
    const { error, data } = await supabase.rpc('start_path_enrollment', {
        p_path_id: pathID,
        p_person_id: personID,
        p_enrolled_by: enrolledBy || null,
        p_trigger_context: { triggerType: 'MANUAL', eventInstanceID, eventRegistrantPathID },
    });

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "enrollRegistrantInPath", pathID, personID, eventInstanceID, eventRegistrantPathID, error }, `Something went wrong enrolling that person`);
        throw Error();
    }

    Toast.show({ type: 'success', text2: 'Person enrolled in path!' });
    return data;
}

const addEventRegistrantCode = async ({ eventInstanceID, personID, code, createdByPersonID }) => {
    const orgID = await getCurrentOrgID();

    const trimmedCode = (code || '').trim();
    if (!trimmedCode) {
        throw Error('Code cannot be empty.');
    }

    const input = {
        organizationID: orgID,
        eventInstanceID,
        personID,
        code: trimmedCode,
        isActive: true,
    };
    if (createdByPersonID) input.createdByPersonID = createdByPersonID;

    const { error, data } = await supabase.from('EventRegistrationCodes').insert(input).select('id, code, isActive, createdAt').single();

    if (error) {
        // Postgres unique violation (23505) or PostgREST duplicate-key message on the org+code index
        const isDuplicate = error.code === '23505' ||
            (error.message && error.message.includes('duplicate key value')) ||
            (error.message && error.message.includes('EventRegistrationCodes_org_code_key'));
        if (isDuplicate) {
            throw Error('This code is already linked to a registration.');
        }
        ErrorHandler(Error(error.message), { mutation: "addEventRegistrantCode", input, error }, `Something went wrong linking the registration code`);
        throw error;
    }

    return data;
}

const setEventRegistrantCodeActive = async ({ codeID, isActive }) => {
    const orgID = await getCurrentOrgID();
    const { error } = await supabase
        .from('EventRegistrationCodes')
        .update({ isActive, revokedAt: isActive ? null : new Date().toISOString() })
        .eq('id', codeID)
        .eq('organizationID', orgID);

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "setEventRegistrantCodeActive", codeID, isActive, error }, `Something went wrong updating the registration code`);
        throw error;
    }

    return { codeID, isActive };
}

const removeEventRegistrant = async ({personID, eventInstanceID}) => {
    const {error} = await supabase.from('EventRegistrants').delete().eq('personID', personID).eq('eventInstanceID', eventInstanceID);

    if(error){
        ErrorHandler(Error(error.message), {mutation: "removeEventRegistrant", personID, eventInstanceID, error}, `Something went wrong removing the event registrant`);
        throw Error;
    }

    return {personID}
}

const getPendingEventRegistrants = async (eventInstanceID) => {
    const {error, data} = await supabase.from('PendingEventRegistrants')
        .select('id, firstName, lastName, email, phone, birthdate, source, submittedAt, groupID, qrToken')
        .eq('eventInstanceID', eventInstanceID)
        .order('submittedAt', { ascending: true });

    if(error){
        ErrorHandler(Error(error.message), {query: "getPendingEventRegistrants", eventInstanceID, error}, `Something went wrong fetching pending registrants`)
        return [];
    }

    return data;
}

const resolvePendingRegistrant = async ({pendingID, eventInstanceID, personID, createNew}) => {
    const orgID = await getCurrentOrgID();

    let resolvedPersonID = personID;
    let groupID = null;
    let qrToken = null;

    if (createNew) {
        // Fetch the pending record to get person details
        const {data: pending, error: fetchError} = await supabase
            .from('PendingEventRegistrants')
            .select('firstName, lastName, email, phone, birthdate, groupID, qrToken')
            .eq('id', pendingID)
            .maybeSingle();

        if (fetchError) {
            ErrorHandler(Error(fetchError.message), {mutation: "resolvePendingRegistrant", pendingID, fetchError}, `Something went wrong fetching pending record`);
            throw Error;
        }

        // Missing pending record is an expected race — another device/session
        // may have already resolved or removed it. Log, don't report.
        if (!pending) {
            Sentry.logger.warn('Pending registrant record not found — likely already resolved or removed', {mutation: "resolvePendingRegistrant", pendingID});
            throw new Error('Pending record not found');
        }

        const personData = {
            organizationID: orgID,
            firstName: pending.firstName,
            lastName: pending.lastName || '',
            email: pending.email || undefined,
        };
        if (pending.birthdate) {
            personData.dateOfBirth = pending.birthdate;
        }

        const {data: newPerson, error: createError} = await supabase
            .from('People')
            .insert(personData)
            .select('id')
            .single();

        if (createError || !newPerson) {
            ErrorHandler(Error(createError?.message), {mutation: "resolvePendingRegistrant-create", pendingID, createError}, `Something went wrong creating person`);
            throw Error;
        }

        resolvedPersonID = newPerson.id;

        // Phones live in PeoplePhones — People.phone is a legacy varchar[]
        // column and writing a scalar to it fails with 22P02. Match the
        // forms.js convention: strip non-digits, insert only if non-empty,
        // and don't fail the mutation if the phone insert errors.
        const errors = [];
        if (pending.phone) {
            const normalized = String(pending.phone).replace(/\D/g, '');
            if (normalized) {
                const {error: phoneError} = await supabase.from('PeoplePhones').insert({
                    organizationID: orgID,
                    personID: newPerson.id,
                    phoneNumber: normalized,
                    label: null,
                    smsOptInStatus: 'never_opted_in',
                    private: false,
                });
                if (phoneError) errors.push(phoneError);
            }
        }

        if (errors.length > 0) {
            ErrorHandler(Error('One or more errors encountered creating a person from pending registrant'), {mutation: "resolvePendingRegistrant", pendingID, errors}, `We created the person, but something went wrong creating related records`);
        }

        groupID = pending.groupID;
        qrToken = pending.qrToken;
    } else {
        // Fetch the pending record to get groupID and qrToken
        const {data: pending, error: fetchError} = await supabase
            .from('PendingEventRegistrants')
            .select('groupID, qrToken')
            .eq('id', pendingID)
            .maybeSingle();

        if (fetchError) {
            ErrorHandler(Error(fetchError.message), {mutation: "resolvePendingRegistrant", pendingID, fetchError}, `Something went wrong fetching pending record`);
            throw Error;
        }

        // Missing pending record is an expected race — another device/session
        // may have already resolved or removed it. Log, don't report.
        if (!pending) {
            Sentry.logger.warn('Pending registrant record not found — likely already resolved or removed', {mutation: "resolvePendingRegistrant", pendingID});
            throw new Error('Pending record not found');
        }

        groupID = pending.groupID;
        qrToken = pending.qrToken;
    }

    // Register the person with groupID and qrToken if available
    const registrantData = {
        organizationID: orgID,
        eventInstanceID,
        personID: resolvedPersonID,
        source: 'webhook',
    };
    if (groupID) registrantData.groupID = groupID;
    if (qrToken) registrantData.qrToken = qrToken;

    const {error: registrantError} = await supabase.from('EventRegistrants').upsert(
        registrantData,
        { onConflict: 'eventInstanceID,personID', ignoreDuplicates: true }
    );

    if (registrantError) {
        ErrorHandler(Error(registrantError.message), {mutation: "resolvePendingRegistrant-register", pendingID, registrantError}, `Something went wrong registering person`);
        throw Error;
    }

    // Delete the pending record
    const {error: deleteError} = await supabase.from('PendingEventRegistrants').delete().eq('id', pendingID);

    if (deleteError) {
        ErrorHandler(Error(deleteError.message), {mutation: "resolvePendingRegistrant-delete", pendingID, deleteError}, `Something went wrong removing pending record`);
    }

    return { pendingID, personID: resolvedPersonID };
}

const deletePendingEventRegistrant = async ({pendingID}) => {
    const {error} = await supabase.from('PendingEventRegistrants').delete().eq('id', pendingID);

    if (error) {
        ErrorHandler(Error(error.message), {mutation: "deletePendingEventRegistrant", pendingID, error}, `Something went wrong removing pending registration`);
        throw Error;
    }

    return { pendingID };
}

// Fallback messages for checkin-by-qr error codes, used when the response
// body can't be read. Server-provided `error` text is preferred when present.
const CHECK_IN_QR_ERROR_MESSAGES = {
    REGISTRATION_PENDING: 'This registration is still pending review. A leader needs to link it before check-in.',
    NO_REGISTRANTS: 'No registration found for this QR code.',
    BADGE_CODE_NOT_FOUND: "This badge code isn't linked to an active registration.",
    ACCESS_DENIED: "You don't have permission to check people in.",
    REGISTRATION_CLOSED: 'Registration is closed for this event.',
    INVALID_TOKEN: 'This QR code is invalid or expired.',
    INVALID_PAYLOAD: 'This QR code is invalid or expired.',
};
const CHECK_IN_QR_ERROR_DEFAULT = "Couldn't verify this QR code. Please try again.";

const checkInByQR = async ({qrPayload, scannerEventInstanceID}) => {
    const { data, error } = await supabase.functions.invoke('checkin-by-qr', {
        body: { qrPayload, scannerEventInstanceID },
    });

    if (error) {
        console.error('checkInByQR: Error from edge function:', error);

        // On non-2xx, supabase-js returns FunctionsHttpError with the original
        // Response on error.context. Body shape: { success: false, error, code }.
        let serverMessage = null;
        let serverCode = null;
        try {
            const body = await error.context?.json();
            if (body?.error) serverMessage = body.error;
            if (body?.code) serverCode = body.code;
        } catch (parseError) {
            console.error('checkInByQR: Failed to parse error body:', parseError);
        }

        const friendlyMessage = serverMessage || CHECK_IN_QR_ERROR_MESSAGES[serverCode] || CHECK_IN_QR_ERROR_DEFAULT;

        // Known expected codes (scanned a non-badge QR, pending registration, ...)
        // are user errors — show the friendly message without reporting to Sentry.
        const isExpectedCode = serverCode && CHECK_IN_QR_ERROR_MESSAGES[serverCode];
        if(!isExpectedCode){
            ErrorHandler(Error(serverMessage || error.message || 'Check-in failed'), {mutation: "checkInByQR", qrPayload, code: serverCode, error}, `Failed to check in via QR code`);
        }

        const friendlyError = new Error(friendlyMessage);
        friendlyError.code = serverCode;
        throw friendlyError;
    }

    return data;
}

const checkInPerson = async ({personID, eventInstanceID, locationID}) => {
    const orgID = await getCurrentOrgID();

    const insertPayload = {
        organizationID: orgID,
        eventInstanceID,
        attendeeID: personID,
        submitterID: personID,
    };
    if (locationID) insertPayload.locationID = locationID;

    const { error } = await supabase.from('Attendance').insert(insertPayload);

    if (error) {
        // Handle conflict as "already checked in" - update location if provided
        const isConflict = error.code === '23505' ||
                          error.message?.includes('duplicate') ||
                          error.message?.includes('conflict');

        if (isConflict && locationID) {
            await supabase.from('Attendance')
                .update({ locationID })
                .eq('attendeeID', personID)
                .eq('eventInstanceID', eventInstanceID);
            return { alreadyCheckedIn: true, locationUpdated: true };
        }

        if (isConflict) {
            return { alreadyCheckedIn: true, locationUpdated: false };
        }

        ErrorHandler(Error(error.message), {mutation: "checkInPerson", personID, eventInstanceID, locationID, error}, `Failed to check in person`);
        throw error;
    }

    return { alreadyCheckedIn: false, locationUpdated: false };
}

const updateAnonymousHeadCount = async ({ eventInstanceID, anonymousHeadCount }) => {
    const { error, data } = await supabase
        .from('EventInstances')
        .update({ anonymousHeadCount })
        .eq('id', eventInstanceID)
        .select('anonymousHeadCount')
        .single();

    if (error) {
        ErrorHandler(Error(error.message), { mutation: "updateAnonymousHeadCount", input: { eventInstanceID, anonymousHeadCount }, error }, `Something went wrong updating the anonymous headcount`);
        throw error;
    }

    return data;
}

const getEventRegistrationExtensions = async ({ eventInstanceID }) => {
    const { data, error } = await supabase
        .from('EventRegistrationExtensions')
        .select(`
            extendedEventInstanceID,
            ExtendedInstance:EventInstances!extendedEventInstanceID(
                id, startTime, endTime, nameOverride, canceled, isDeleted,
                Event:Events(name)
            )
        `)
        .eq('mainEventInstanceID', eventInstanceID)
        .order('createdAt', { ascending: true });
    if (error) {
        ErrorHandler(Error(error.message), { mutation: "getEventRegistrationExtensions", eventInstanceID, error }, 'Failed to fetch registration extensions');
        throw error;
    }
    return data;
};

const getEventRegistrationExtensionSource = async ({ eventInstanceID }) => {
    const { data, error } = await supabase
        .from('EventRegistrationExtensions')
        .select(`
            mainEventInstanceID,
            MainInstance:EventInstances!mainEventInstanceID(
                id, startTime, endTime, nameOverride,
                Event:Events(name)
            )
        `)
        .eq('extendedEventInstanceID', eventInstanceID)
        .maybeSingle();
    if (error) {
        ErrorHandler(Error(error.message), { mutation: "getEventRegistrationExtensionSource", eventInstanceID, error }, 'Failed to fetch extension source');
        throw error;
    }
    return data;
};

const PUBLIC_EVENT_STAT_KEYS = [
    'totalAttendance',
    'uniqueAttendance',
    'adultsAttending',
    'kidsAttending',
    'spiritBaptism',
    'waterBaptism',
    'guests',
    'hostChurch',
    'anotherApostolicChurch',
];

const normalizePublicEventStats = (stats, includeUniqueAttendance = false) => PUBLIC_EVENT_STAT_KEYS.reduce((result, key) => {
    if (key === 'uniqueAttendance' && !includeUniqueAttendance) return result;
    result[key] = Number.isInteger(stats?.[key]) ? stats[key] : 0;
    return result;
}, {});

const normalizeReferralSources = (sources) => Array.isArray(sources)
    ? sources
        .filter((source) => typeof source?.label === 'string' && Number.isInteger(source?.count))
        .map((source) => ({ label: source.label, count: source.count }))
    : [];

const getPublicJesusTentRevivalStats = async () => {
    const rpc = 'public_get_jesus_tent_revival_stats';
    const { data, error } = await supabase.rpc(rpc);

    if (error) {
        const rpcError = Error(error.message || 'Unable to load event totals');
        ErrorHandler(rpcError, { mutation: 'getPublicJesusTentRevivalStats', rpc, error });
        throw rpcError;
    }

    if (!data?.event || !Array.isArray(data?.dates) || !data?.overall) {
        // Anticipated state (event/instances not ready yet) — the page renders
        // its unavailable state from this error. Not reported to Sentry.
        throw Error('Event totals are unavailable');
    }

    return {
        event: {
            name: data.event.name,
            timezone: data.event.timezone,
        },
        dates: data.dates.map((date) => ({
            key: date.key,
            label: date.label,
            dateLabel: date.dateLabel,
            stats: normalizePublicEventStats(date.stats),
        })),
        overall: {
            ...normalizePublicEventStats(data.overall, true),
            referralSources: normalizeReferralSources(data.overall.referralSources),
        },
        updatedAt: data.updatedAt,
    };
};

const saveEventRegistrationExtensions = async ({ mainEventInstanceID, extendedEventInstanceIDs }) => {
    const orgID = await getCurrentOrgID();

    const { data: current, error: fetchError } = await supabase
        .from('EventRegistrationExtensions')
        .select('extendedEventInstanceID')
        .eq('mainEventInstanceID', mainEventInstanceID);
    if (fetchError) throw fetchError;

    const currentIDs = new Set((current || []).map(e => e.extendedEventInstanceID));
    const desiredIDs = new Set(extendedEventInstanceIDs);

    const toAdd = [...desiredIDs].filter(id => !currentIDs.has(id));
    const toRemove = [...currentIDs].filter(id => !desiredIDs.has(id));

    if (toAdd.length > 0) {
        const rows = toAdd.map(extendedEventInstanceID => ({
            organizationID: orgID,
            mainEventInstanceID,
            extendedEventInstanceID,
        }));
        const { error: insertError } = await supabase
            .from('EventRegistrationExtensions')
            .insert(rows);
        if (insertError) throw insertError;
    }

    if (toRemove.length > 0) {
        const { error: deleteError } = await supabase
            .from('EventRegistrationExtensions')
            .delete()
            .eq('mainEventInstanceID', mainEventInstanceID)
            .in('extendedEventInstanceID', toRemove);
        if (deleteError) throw deleteError;
    }

    return { added: toAdd.length, removed: toRemove.length };
};

export {
    getEvents,
    getEvent,
    getEventInstance,
    getMonthlyEventInstances,
    getEventInstancesForDate,
    getEventCustomFields,
    getEventCustomFieldSections,
    saveEventCustomField,
    saveEventCustomFieldSection,
    deleteEventCustomField,
    deleteEventCustomFieldSection,
    createEventCustomField,
    updateEventCustomField,
    cancelEventInstance,
    deleteEventInstance,
    deleteEvent,
    saveEvent,
    saveEventInstance,
    createAdditionalEventInstance,
    getAttendance,
    getRecentAttendance,
    getPersonAttendanceHistory,
    addAttendance,
    markAttendancePrinted,
    removeAttendance,
    removeAttendanceFromExtensionSet,
    resolveCheckinTarget,
    getExtensionInstanceIDs,
    checkOutAttendance,
    getBlackoutDates,
    getBlackoutDatesForMonth,
    createFolderForEvent,
    createBlackoutPeriod,
    updateBlackoutPeriod,
    deleteBlackoutPeriod,
    getPrintLabels,
    getEventCustomFieldSection,
    getEventAnalytics,
    getEventUniqueAttendees,
    getEventUniqueAttendeesDetail,
    getEventInstancesByEventID,
    getEventInstancesForRegistration,
    getUpcomingEventInstancesWithTasks,
    getPastEventInstances,
    getEventForMerge,
    mergeEvents,
    getEventRegistrants,
    addEventRegistrant,
    getEventRegistrantCodes,
    getEventRegistrantPaths,
    addEventRegistrantPath,
    removeEventRegistrantPath,
    getRegistrantPathEnrollments,
    getRegistrantDetail,
    enrollRegistrantInPath,
    addEventRegistrantCode,
    setEventRegistrantCodeActive,
    removeEventRegistrant,
    getPendingEventRegistrants,
    resolvePendingRegistrant,
    deletePendingEventRegistrant,
    checkInByQR,
    checkInPerson,
    updateAnonymousHeadCount,
    getEventRegistrationExtensions,
    getEventRegistrationExtensionSource,
    getPublicJesusTentRevivalStats,
    saveEventRegistrationExtensions,
    checkLocationConflicts,
    buildLocationCheckRanges,
    describeLocationConflicts,
    getLocationScheduleForDay,
}